import { mapPolygonPoints } from "@constructionautomation/backend/convex/lib/measurementGeometry";
import { getOrganizationAccessError } from "@/lib/organization-access-errors";
import { notifyError } from "@/lib/notify-error";
import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import { convexQuery } from "@convex-dev/react-query";
import { useConvex, useMutation } from "convex/react";
import { ArrowDown, ArrowUp, CalendarClock, Dot, FileText, ListOrdered, Plus, Ruler, Search, SlidersHorizontal, Trash2 } from "lucide-react";
import { toast } from "sonner";

import { api } from "@constructionautomation/backend";
import type { Doc, Id } from "@constructionautomation/backend/convex/_generated/dataModel";
import { Button } from "@/components/ui/button";
import { IconButton } from "@/components/ui/icon-button";
import { UndoRedoControls } from "@/components/undo-redo-controls";
import { ColorPicker } from "@/components/ui/color-picker";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { HoverCard, HoverCardContent, HoverCardTrigger } from "@/components/ui/hover-card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { MultiSelect, type MultiSelectSection } from "@/components/ui/multi-select";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  DEFAULT_FIELD_PILOT_SCHEDULE_COLUMNS,
} from "@constructionautomation/backend/convex/lib/exportArtifactOptions";
import { DocumentViewportHost } from "@/features/document-viewport/document-viewport-host";
import { getViewportContentCenter, MAX_VIEWPORT_ZOOM } from "@/features/document-viewport/utils";
import { DocumentTagEditorOverlay } from "@/features/document-intelligence/document-tag-editor-overlay";
import { SearchDocumentTagsDialog } from "@/features/document-intelligence/search-document-tags-dialog";
import { TagInstanceNavigationButton } from "@/features/document-intelligence/tag-instance-navigation-button";
import {
  comparePlanSetTagDetections,
  useTagInstanceNavigation,
} from "@/features/document-intelligence/use-tag-instance-navigation";
import { FieldPilotAddCustomLineItemDialog } from "@/features/field-pilot/add-custom-line-item-dialog";
import { ConvertTagPointsDialog } from "@/features/field-pilot/convert-tag-points-dialog";
import { FieldPilotDailyLogsDialog } from "@/features/field-pilot/daily-logs-dialog";
import {
  FIELD_PILOT_TAG_SEARCH_EMPTY_MESSAGES,
  fieldPilotDetectionsApi,
  formatConvertActionLabel,
  getFieldPilotDetectionStyle,
  mapFieldPilotSearchToDialogResponse,
  summarizeLocationsForTag,
} from "@/features/field-pilot/field-pilot-tag-adapter";
import {
  FieldPointOverlay,
  type DraftPoint,
} from "@/features/field-pilot/field-point-overlay";
import { FieldPilotLayersMenu } from "@/features/field-pilot/layers-menu";
import { ProgressRowActionsMenu } from "@/features/field-pilot/progress-row-actions-menu";
import { formatProgressValue, measurementTypeByTagIdFromItems } from "@/features/field-pilot/progress-value-format";
import {
  FIELD_PILOT_COLUMN_BAND,
  FIELD_PILOT_ROW_CELL,
  FieldPilotTreeCellContent,
  fieldPilotRowClasses,
} from "@/features/field-pilot/row-hierarchy";
import { FieldPilotScheduleDialog } from "@/features/field-pilot/schedule-dialog";
import { FieldPilotTagScheduleDialog } from "@/features/field-pilot/tag-schedule-dialog";
import {
  formatDurationDays,
  formatScheduleDate,
  formatSchedulePercent,
  withFreshScheduleTiming,
  type FieldPilotScheduleRow,
} from "@/features/field-pilot/schedule-format";
import { useScheduleClock } from "@/features/field-pilot/use-schedule-clock";
import {
  ScheduleProgressBar,
  StatusBreakdownBar,
} from "@/features/field-pilot/schedule-visualizations";
import {
  splitTaskChildRows,
  SubTaskRows,
  UngroupedRow,
  type FieldPilotScheduleRowWithSubTasks,
} from "@/features/field-pilot/sub-task-rows";
import {
  FieldPilotSubTaskDialog,
  type FieldPilotSubTaskDialogTarget,
} from "@/features/field-pilot/sub-task-dialog";
import {
  useFieldPilotDisplayOptions,
  type FieldPilotColorizeBy,
} from "@/features/field-pilot/use-field-pilot-display-options";
import { useFieldPilotLayers } from "@/features/field-pilot/use-field-pilot-layers";
import { fieldPilotExportOptionsFromDisplay } from "@/features/field-pilot/export-display-seed";
import {
  resolveItemAnnotation,
  resolveItemLabel,
  resolveTagLabel,
  sharedSelectValue,
  type FieldPilotAnnotationMode,
} from "@/features/field-pilot/label-utils";
import {
  FieldSelectedItemCard,
  NONE_SUB_TASK_OPTION,
} from "@/features/field-pilot/selected-item-card";
import { StatusOptionLabel } from "@/features/field-pilot/status-dot";
import {
  buildTagColorMap,
  FIELD_PILOT_TAG_FALLBACK_COLOR,
} from "@/features/field-pilot/color-utils";
import { useDocumentViewport } from "@/features/document-viewport/context";
import type { DocumentSource, DocumentViewportValue } from "@/features/document-viewport/types";
import { FloatingPanelToggle } from "@/features/takeoff-pilot/floating-panel-toggle";
import { useSnapArtifactsAvailable } from "@/features/snapping/use-snap-artifacts-available";
import {
  getBottomPanelMaxHeight,
  ResizableBottomPanel,
} from "@/features/takeoff-pilot/resizable-bottom-panel";
import { ExportButton } from "@/features/exports/export-button";
import { ExportDialog, lastFileFromArtifact } from "@/features/exports/export-dialog";
import { ExportOptionCheckbox, ExportOptionGroup, ExportOptionRow } from "@/features/exports/export-options";
import { exportTarget } from "@/features/exports/export-names";
import { useExportFilenames } from "@/features/exports/use-export-filenames";
import { ResultsPanelHeader } from "@/features/document-viewport/results-panel-header";
import { ViewerPager, ViewerSourceSelect } from "@/features/document-viewport/viewer-source-controls";
import { PacketSourceSheetButton } from "@/features/document-viewport/packet-source-sheet-button";
import { PreparingDocumentsState, resolveActiveViewerSource } from "@/features/document-viewport/preparing-documents";
import { useRevisionSheetLabels } from "@/features/sheet-index/use-revision-sheets";
import { useMeasurementScale } from "@/hooks/useMeasurementScale";
import { useUndoRedoHistory } from "@/hooks/use-undo-redo-history";
import { CALIBRATION_METHOD_IDS, CalibrationOverlay, useScaleToolsetRailController } from "@/viewport-tools/scale";
import { DEFAULT_TEXT_FONT_SIZE } from "@/hooks/useMeasurementTextLock";
import { computeMeasurementValue, parseFeetInches } from "@/lib/geometry";
import { withExportExtension } from "@/lib/export-filenames";
import { GLOBAL_PROCESSING_COMPLETED, GLOBAL_PROCESSING_FAILED, GLOBAL_PROCESSING_PENDING, GLOBAL_PROCESSING_PROCESSING, type ProcessingStateWithIdle } from "@/lib/processing-states";
import { useViewerExit } from "@/components/layout/use-viewer-exit";
import { ViewerBackButton, ViewerTopBar } from "@/components/layout/viewer-top-bar";
import { SpinnerState } from "@/components/page-states";
import { useRouteParam } from "@/lib/use-route-param";
import { getUserFacingErrorMessage } from "@/lib/user-facing-errors";
import { cn } from "@/lib/utils";
import { CountBadge } from "@/components/semantic-badges";
import { StatusDot } from "@/components/status-dot";
import { SegmentedControl, type SegmentedControlOption } from "@/components/ui/segmented-control";
import { MEASUREMENT_TYPES, type MeasurementType, type ToolType } from "@/viewport-tools/measurement";
import { useMeasurementToolsetRailController } from "@/viewport-tools/measurement";
import { FieldMeasurementOverlay, type FieldMeasurementOverlayHandle } from "@/features/field-pilot/field-measurement-overlay";
import {
  type DraftMeasurementState,
  type MeasurementAnnotation,
  type MeasurementGeometry,
  type MeasurementOverlayRecord,
  type PolygonGeometry,
} from "@/viewport-tools/measurement/overlay/measurement-overlay-types";
import { measurementRequiresHeight } from "@/viewport-tools/measurement/overlay/measurement-overlay-helpers";
import {
  TAG_TOOL_IDS,
  type DocumentTagOption,
  type DocumentTagPlacementMode,
  type TagToolId,
  useTagsToolsetRailController,
} from "@/viewport-tools/tags";
import { ViewportToolsetsHeader } from "@/viewport-tools/viewport-toolsets-header";
import { ViewportToolsetsRail } from "@/viewport-tools/viewport-toolsets-rail";
import { VIEWPORT_TOOLSET_IDS, VIEWPORT_TOOLSET_PLACEMENTS, type ViewportToolsetId } from "@/viewport-tools/core";
import { useViewportToolsetControllers } from "@/viewport-tools/use-viewport-toolset-controllers";
import { getViewportToolsetsForPlacement, VIEWPORT_VIEW_IDS } from "@/viewport-tools/view-config";
import { useCanEdit, useOnEditAccessLost } from "@/lib/capabilities";

export const Route = createFileRoute("/fieldPilot/$jobId")({
  component: FieldPilotJobPage,
});

function getMediaKind(filename: string): DocumentSource["mediaKind"] {
  const lower = filename.toLowerCase();
  if (lower.endsWith(".pdf")) return "pdf";
  if (/\.(png|jpe?g|webp|gif)$/.test(lower)) return "image";
  return "unsupported";
}

const TAG_COLLATOR = new Intl.Collator(undefined, { numeric: true, sensitivity: "base" });
const FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM = {
  actions: 2,
  // The task column is the tree column: indent, toggle, kind badge, name, instance nav.
  task: 24,
  total: 12,
  schedule: 8,
  scheduleProgress: 10,
  statusBreakdown: 14,
} as const;
const FIELD_PROGRESS_TABLE_BASE_WIDTH_REM =
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.actions +
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.task +
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.total +
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.statusBreakdown;
const FIELD_PROGRESS_TABLE_SCHEDULE_WIDTH_REM =
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.schedule * 5 +
  FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.scheduleProgress;

function compareTagsByRawValue(a: { rawValue: string }, b: { rawValue: string }) {
  return TAG_COLLATOR.compare(a.rawValue, b.rawValue);
}

function getTagPlacementMode(activeTagTool: TagToolId | null): DocumentTagPlacementMode {
  if (activeTagTool === TAG_TOOL_IDS.add) return "add";
  if (activeTagTool === TAG_TOOL_IDS.duplicate) return "duplicate";
  return null;
}

function isFieldHistoryEnabled(pendingEditCount: number) {
  return pendingEditCount === 0;
}

function isFieldHistoryControlBusy(isBusy: boolean, pendingEditCount: number) {
  return isBusy || pendingEditCount > 0;
}

const COLORIZE_BY_OPTIONS: ReadonlyArray<SegmentedControlOption<FieldPilotColorizeBy>> = [
  { value: "status", label: "Status" },
  { value: "tag", label: "Tag" },
];

// Read-only schedule cells appended to the main progress table when the toggle is on.
function ScheduleColumns({ scheduleRow }: { scheduleRow?: FieldPilotScheduleRow }) {
  if (!scheduleRow) {
    return (
      <>
        {Array.from({ length: 6 }).map((_, index) => (
          <TableCell
            key={index}
            className={cn(
              FIELD_PILOT_ROW_CELL,
              index === 0 && FIELD_PILOT_COLUMN_BAND,
              "text-muted-foreground"
            )}
          >
            —
          </TableCell>
        ))}
      </>
    );
  }
  return (
    <>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, FIELD_PILOT_COLUMN_BAND, "whitespace-nowrap")}>
        {formatScheduleDate(scheduleRow.startDateKey)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {formatScheduleDate(scheduleRow.finishDateKey)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {formatDurationDays(scheduleRow.durationDays)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap")}>
        {scheduleRow.hasSchedule ? formatSchedulePercent(scheduleRow.expectedFraction) : "—"}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "whitespace-nowrap font-semibold")}>
        {formatSchedulePercent(scheduleRow.actualFraction)}
      </TableCell>
      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "min-w-[140px]")}>
        <ScheduleProgressBar row={scheduleRow} />
      </TableCell>
    </>
  );
}

function getGeometryQuantity(
  tool: "point" | ToolType,
  geometry: FieldPointGeometry | OverlayGeometry,
  measurementType: MeasurementType,
  heightFeet: number,
  pixelsPerFoot: number,
  pageAspectRatio: number | undefined
) {
  if (geometry.type === "point") return 1;
  if (tool === "point") return 1;
  return computeMeasurementValue(tool, measurementType, geometry, heightFeet, pixelsPerFoot, pageAspectRatio);
}

type FieldTool = "point" | ToolType;
type FieldGeometry = Doc<"fieldPilotItems">["geometry"];
type FieldGeometryKind = Doc<"fieldPilotItems">["geometryKind"];
type FieldPointGeometry = { type: "point"; x: number; y: number };
type OverlayGeometry = MeasurementGeometry | PolygonGeometry;
function getFieldItemCreateArgs(item: Doc<"fieldPilotItems">) {
  const renderFrame =
    item.renderFrameVersion && item.renderFrameBoxType && item.renderFrameYAxis
      ? {
          version: item.renderFrameVersion,
          boxType: item.renderFrameBoxType,
          yAxis: item.renderFrameYAxis,
          rasterWidthPx: item.renderFrameRasterWidthPx,
          rasterHeightPx: item.renderFrameRasterHeightPx,
        }
      : undefined;
  return {
    jobId: item.jobId,
    planDocumentId: item.planDocumentId!,
    pageNumber: item.pageNumber,
    tagId: item.tagId,
    statusId: item.statusId,
    itemKind: item.itemKind,
    geometryKind: item.geometryKind,
    geometry: item.geometry,
    measurementType: item.measurementType,
    quantityValue: item.quantityValue,
    renderFrame,
    annotation: item.annotation,
    height: item.height,
  };
}

const FIELD_POINT_TOOL_ID = "point";

function isMeasurementTool(tool: FieldTool | null): tool is ToolType {
  return tool === "line" || tool === "polyline" || tool === "rectangle" || tool === "polygon";
}

function toFieldGeometry(
  geometryKind: FieldGeometryKind,
  geometry: FieldPointGeometry | OverlayGeometry
): FieldGeometry {
  if (geometry.type === "point") return geometry;
  if (geometry.type === "rect") return { ...geometry, type: geometryKind as "rectangle" | "bbox" };
  return { ...geometry, type: geometryKind as "line" | "polyline" | "polygon" };
}

function toOverlayGeometry(geometry: FieldGeometry): OverlayGeometry | null {
  if (geometry.type === "point") return null;
  if (geometry.type === "rectangle" || geometry.type === "bbox") {
    return {
      type: "rect",
      x: geometry.x,
      y: geometry.y,
      width: geometry.width,
      height: geometry.height,
    };
  }
  return { ...geometry, type: "poly" };
}

function fieldGeometryUsesNormalizedCoordinates(geometry: FieldGeometry) {
  if (geometry.type === "point") return Math.max(Math.abs(geometry.x), Math.abs(geometry.y)) <= 2;
  if (geometry.type === "rectangle" || geometry.type === "bbox") {
    return Math.max(Math.abs(geometry.x), Math.abs(geometry.y), Math.abs(geometry.width), Math.abs(geometry.height)) <= 2;
  }
  return geometry.vertices.every((point) => Math.max(Math.abs(point.x), Math.abs(point.y)) <= 2);
}

function translateFieldGeometry(geometry: FieldGeometry): FieldGeometry {
  const offset = fieldGeometryUsesNormalizedCoordinates(geometry) ? 0.02 : 24;
  if (geometry.type === "point") return { ...geometry, x: geometry.x + offset, y: geometry.y + offset };
  if (geometry.type === "rectangle" || geometry.type === "bbox") return { ...geometry, x: geometry.x + offset, y: geometry.y + offset };
  return { ...mapPolygonPoints({ ...geometry, type: "poly" }, point => ({ x: point.x + offset, y: point.y + offset })), type: geometry.type };
}

const FIELD_PILOT_TEXT_SETTINGS_KEY_PREFIX = "field-pilot-measurement-text-settings";
const FIELD_PILOT_SCHEDULE_COLUMNS_KEY_PREFIX = "field-pilot-schedule-columns";

type FieldPilotNavDetection = {
  _id: Id<"documentIntelligenceDetections">;
  normalizedText: string;
  pageNumber: number;
  documentOrder: number;
  bbox: { x: number; y: number; width: number; height: number };
  planDocumentId: Id<"fieldPilotPlanDocuments">;
  planDocumentName: string;
};

function FieldViewportBinder({
  onViewportChange,
}: {
  onViewportChange: (viewport: DocumentViewportValue) => void;
}) {
  const viewport = useDocumentViewport();
  useEffect(() => {
    onViewportChange(viewport);
  }, [onViewportChange, viewport]);
  return null;
}

function FieldPointDrawingLayer({
  activeTool,
  onCreatePoint,
}: {
  activeTool: FieldTool | null;
  onCreatePoint: (point: DraftPoint) => Promise<void>;
}) {
  const { workspaceSize, transforms } = useDocumentViewport();

  if (activeTool !== FIELD_POINT_TOOL_ID) {
    return null;
  }

  return (
    <svg
      className="absolute inset-0 z-20"
      width={workspaceSize.width}
      height={workspaceSize.height}
      onPointerDown={(event) => {
        if (event.target !== event.currentTarget) return;
        const rect = event.currentTarget.getBoundingClientRect();
        const point = transforms.screenToPagePoint({
          x: event.clientX - rect.left,
          y: event.clientY - rect.top,
        });
        if (point) {
          void onCreatePoint(point);
        }
      }}
    />
  );
}

function FieldPilotStatusEditorRow({
  status,
  index,
  total,
  onUpdateStatus,
  onReorderStatus,
  onDeleteStatus,
}: {
  status: Doc<"fieldPilotStatuses"> & { measurementCount?: number; milestoneCount?: number };
  index: number;
  total: number;
  onUpdateStatus: (args: {
    statusId: Id<"fieldPilotStatuses">;
    name?: string;
    colorHex?: string;
  }) => Promise<unknown>;
  onReorderStatus: (statusId: Id<"fieldPilotStatuses">, direction: "up" | "down") => void;
  onDeleteStatus: (statusId: Id<"fieldPilotStatuses">) => Promise<unknown>;
}) {
  const [draftName, setDraftName] = useState(status.name);
  const [draftColor, setDraftColor] = useState(status.colorHex);
  const [isSaving, setIsSaving] = useState(false);
  const skipNextBlurSaveRef = useRef(false);

  useEffect(() => {
    setDraftName(status.name);
  }, [status.name]);

  useEffect(() => {
    setDraftColor(status.colorHex);
  }, [status.colorHex]);

  const saveName = async () => {
    if (isSaving) return;
    const nextName = draftName.trim();
    if (!nextName) {
      setDraftName(status.name);
      toast.error("Status name is required");
      return;
    }
    if (nextName === status.name) {
      setDraftName(nextName);
      return;
    }

    setIsSaving(true);
    try {
      await onUpdateStatus({ statusId: status._id, name: nextName });
      setDraftName(nextName);
      toast.success("Status renamed");
    } catch (error) {
      setDraftName(status.name);
      notifyError(error, getUserFacingErrorMessage(error, "Failed to rename status"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleColorChange = async (nextColor: string) => {
    const normalizedColor = nextColor.toUpperCase();
    const previousColor = draftColor;
    setDraftColor(normalizedColor);
    if (normalizedColor === status.colorHex.toUpperCase()) return;

    setIsSaving(true);
    try {
      await onUpdateStatus({ statusId: status._id, colorHex: normalizedColor });
    } catch (error) {
      setDraftColor(previousColor);
      notifyError(error, getUserFacingErrorMessage(error, "Failed to update status color"));
    } finally {
      setIsSaving(false);
    }
  };

  const handleDelete = async () => {
    setIsSaving(true);
    try {
      await onDeleteStatus(status._id);
      toast.success("Status deleted");
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to delete status"));
    } finally {
      setIsSaving(false);
    }
  };

  const measurementCount = status.measurementCount ?? 0;
  const milestoneCount = status.milestoneCount ?? 0;
  const isProtectedDefaultStatus = status.isDefault === true;
  const hasStatusReferences = measurementCount > 0 || milestoneCount > 0;
  const isDeleteDisabled = isSaving || hasStatusReferences || isProtectedDefaultStatus;
  const deleteTooltip = hasStatusReferences ? (
    <>
      <span className="font-semibold">Status in use</span>
      <span>
        Clear {measurementCount.toLocaleString()} measurement{measurementCount === 1 ? "" : "s"}
        {milestoneCount > 0
          ? ` and ${milestoneCount.toLocaleString()} milestone${milestoneCount === 1 ? "" : "s"}`
          : ""} first.
      </span>
    </>
  ) : isProtectedDefaultStatus ? (
    <>
      <span className="font-semibold">Default status</span>
      <span>Cannot be deleted.</span>
    </>
  ) : null;
  const deleteButton = (
    <IconButton
      label={`Delete ${status.name}`}
      icon={Trash2}
      // The disabled-reason tooltip below replaces the default one.
      tooltip={deleteTooltip ? false : undefined}
      iconClassName={isDeleteDisabled ? "text-muted-foreground" : "text-destructive"}
      onClick={() => void handleDelete()}
      disabled={isDeleteDisabled}
    />
  );

  return (
    <div className="flex items-center justify-between gap-3 rounded-md border px-3 py-2">
      <div className="flex min-w-0 flex-1 items-center gap-2 text-sm font-medium">
        <span className="w-5 shrink-0 text-xs tabular-nums text-muted-foreground">{index + 1}</span>
        <ColorPicker
          value={draftColor}
          onChange={(nextColor) => {
            void handleColorChange(nextColor);
          }}
          disabled={isSaving}
          ariaLabel={`Set color for ${status.name}`}
        />
        <Input
          value={draftName}
          onChange={(event) => setDraftName(event.target.value)}
          onBlur={() => {
            if (skipNextBlurSaveRef.current) {
              skipNextBlurSaveRef.current = false;
              return;
            }
            void saveName();
          }}
          onKeyDown={(event) => {
            if (event.key === "Enter") {
              event.currentTarget.blur();
            }
            if (event.key === "Escape") {
              skipNextBlurSaveRef.current = true;
              setDraftName(status.name);
              event.currentTarget.blur();
            }
          }}
          disabled={isSaving}
          className="h-8 min-w-0 flex-1"
          aria-label={`Rename ${status.name}`}
        />
      </div>
      <div className="flex shrink-0 items-center gap-1">
        <IconButton
          label={`Move ${status.name} earlier`}
          icon={ArrowUp}
          onClick={() => onReorderStatus(status._id, "up")}
          disabled={index === 0}
        />
        <IconButton
          label={`Move ${status.name} later`}
          icon={ArrowDown}
          onClick={() => onReorderStatus(status._id, "down")}
          disabled={index === total - 1}
        />
        {deleteTooltip ? (
          <Tooltip delayDuration={700}>
            <TooltipTrigger asChild>
              <span className="inline-flex">{deleteButton}</span>
            </TooltipTrigger>
            <TooltipContent className="flex flex-col gap-1 whitespace-nowrap px-2.5 py-2 text-left leading-tight">
              {deleteTooltip}
            </TooltipContent>
          </Tooltip>
        ) : (
          deleteButton
        )}
      </div>
    </div>
  );
}

// eslint-disable-next-line complexity
export function FieldPilotJobPage() {
  // Reviewers see the plan, items, and progress; every edit here is a project write.
  const canEdit = useCanEdit();
  const scheduleNow = useScheduleClock();
  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
  const routeJobId = useRouteParam("jobId") as Id<"fieldPilotJobs"> | undefined;
  const handleExitViewer = useViewerExit({ fallback: { to: "/fieldPilot", title: "FieldPilot" } });
  const [activeSourceId, setActiveSourceId] = useState<string | null>(null);
  const [activePage, setActivePage] = useState(1);
  const [pageCount, setPageCount] = useState(1);
  const [panelHeight, setPanelHeight] = useState(320);
  const [innerHeight, setInnerHeight] = useState(() =>
    typeof window !== "undefined" ? window.innerHeight : 800
  );
  const [activeToolsetId, setActiveToolsetId] = useState<ViewportToolsetId | null>(null);
  const [activeTool, setActiveTool] = useState<FieldTool | null>(null);
  const [activeMeasurementType, setActiveMeasurementType] = useState<MeasurementType>(MEASUREMENT_TYPES.LF);
  const [snapEnabled, setSnapEnabled] = useState(false);
  const [angleLockEnabled, setAngleLockEnabled] = useState(false);
  const [incrementSnapEnabled, setIncrementSnapEnabled] = useState(false);
  const [incrementStepFeet, setIncrementStepFeet] = useState(1);
  const [heightValue, setHeightValue] = useState("");
  const [textLocked, setTextLocked] = useState(false);
  const [textFontSize, setTextFontSize] = useState(DEFAULT_TEXT_FONT_SIZE);
  const [labelsVisible, setLabelsVisible] = useState(true);
  const [selectedTagId, setSelectedTagId] = useState("");
  const [selectedStatusId, setSelectedStatusId] = useState("");
  const [statusFilterId, setStatusFilterId] = useState("all");
  const [expandedTagIds, setExpandedTagIds] = useState<Set<string>>(() => new Set());
  const [isSubTaskDialogOpen, setIsSubTaskDialogOpen] = useState(false);
  const [subTaskDialogTarget, setSubTaskDialogTarget] =
    useState<FieldPilotSubTaskDialogTarget | null>(null);
  const [draftMeasurement, setDraftMeasurement] = useState<DraftMeasurementState | null>(null);
  const [pendingFieldEditCount, setPendingFieldEditCount] = useState(0);
  const fieldMeasurementOverlayRef = useRef<FieldMeasurementOverlayHandle>(null);
  const [selectedItemIds, setSelectedItemIds] = useState<Set<string>>(() => new Set());
  const [selectedTagDetectionIds, setSelectedTagDetectionIds] = useState<string[]>([]);
  const [pendingSelections, setPendingSelections] = useState<Map<string, boolean>>(new Map());
  const [copiedItems, setCopiedItems] = useState<Doc<"fieldPilotItems">[]>([]);
  const [isExportDialogOpen, setIsExportDialogOpen] = useState(false);
  const [saveAsDailyLog, setSaveAsDailyLog] = useState(false);
  const [includeSchedule, setIncludeSchedule] = useState(true);
  const [includeMeasurementBreakdown, setIncludeMeasurementBreakdown] = useState(true);
  const [includeProgressBars, setIncludeProgressBars] = useState(true);
  const [includeGanttChart, setIncludeGanttChart] = useState(true);
  const [exportLabelAnnotation, setExportLabelAnnotation] =
    useState<FieldPilotAnnotationMode>("off");
  const [exportStatusFilterId, setExportStatusFilterId] = useState<string>("all");
  const [exportColorizeBy, setExportColorizeBy] = useState<"status" | "tag">("status");
  const [exportShowMeasurementValues, setExportShowMeasurementValues] = useState(true);
  const [autoDownloadPdfArtifactId, setAutoDownloadPdfArtifactId] = useState<string | null>(null);
  const autoDownloadPdfInFlightRef = useRef<string | null>(null);
  // The name typed for a queued export, so the auto-download doesn't wait on the saved-name query.
  const autoDownloadNameRef = useRef<string | null>(null);
  const [isAddCustomLineItemOpen, setIsAddCustomLineItemOpen] = useState(false);
  const [isScheduleDialogOpen, setIsScheduleDialogOpen] = useState(false);
  const [scheduleEditTagId, setScheduleEditTagId] = useState<Id<"fieldPilotTags"> | null>(null);
  const [showScheduleColumns, setShowScheduleColumns] = useState(false);
  const [isExporting, setIsExporting] = useState(false);
  const [isDailyLogsDialogOpen, setIsDailyLogsDialogOpen] = useState(false);
  const [isManageStatusesOpen, setIsManageStatusesOpen] = useState(false);
  const [newStatusName, setNewStatusName] = useState("");
  const [newStatusColor, setNewStatusColor] = useState("#22C55E");
  const [isCreatingStatus, setIsCreatingStatus] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(true);
  const [highlightedTagIds, setHighlightedTagIds] = useState<Id<"fieldPilotTags">[]>([]);
  const [activeTagTool, setActiveTagTool] = useState<TagToolId | null>(null);
  const [selectedAddTagOptionId, setSelectedAddTagOptionId] = useState<string | null>(null);
  const [isSearchTagsOpen, setIsSearchTagsOpen] = useState(false);
  const [convertDialogTagId, setConvertDialogTagId] = useState<Id<"fieldPilotTags"> | null>(null);
  // A member demoted mid-session drops out of any tool or write dialog they had open.
  useOnEditAccessLost(() => {
    setActiveToolsetId(null);
    setActiveTool(null);
    setActiveTagTool(null);
    setIsExportDialogOpen(false);
    setIsAddCustomLineItemOpen(false);
    setIsScheduleDialogOpen(false);
    setScheduleEditTagId(null);
    setIsManageStatusesOpen(false);
    setIsSearchTagsOpen(false);
    setConvertDialogTagId(null);
    setSubTaskDialogTarget(null);
  });
  const [convertingDetectionId, setConvertingDetectionId] = useState<string | null>(null);
  const pendingDetectionFocusRef = useRef<FieldPilotNavDetection | null>(null);
  const preservePendingDetectionFocusRef = useRef(false);
  const progressRowRefs = useRef(new Map<string, HTMLTableRowElement>());
  const [activePageAspectRatio, setActivePageAspectRatio] = useState<number | undefined>(undefined);
  const [activePagePixelMetrics, setActivePagePixelMetrics] = useState({ width: 0, height: 0 });
  const flushInFlightRef = useRef<Promise<boolean> | null>(null);
  const onHideMeasurements = useCallback(() => {
    setSelectedItemIds(new Set());
    setDraftMeasurement(null);
    setActiveTool(null);
  }, []);
  const { visibility: layerVisibility, setLayerVisible, enableMeasurements, enableTags } =
    useFieldPilotLayers({
      jobId: String(routeJobId ?? ""),
      onHideMeasurements,
    });
  const {
    colorizeBy,
    showMeasurementValues,
    viewportAnnotation,
    setColorizeBy,
    setShowMeasurementValues,
    setViewportAnnotation,
  } = useFieldPilotDisplayOptions({ jobId: String(routeJobId ?? "") });
  const viewportRef = useRef<DocumentViewportValue | null>(null);
  const [loadedTextSettingsKey, setLoadedTextSettingsKey] = useState<string | null>(null);
  const activeTextSettingsKeyRef = useRef<string | null>(null);
  const textLockSaveVersionRef = useRef(0);
  const textFontSizeSaveVersionRef = useRef(0);
  const textSettingsMigrationKeyRef = useRef<string | null>(null);
  const confirmedTextLockedRef = useRef(false);
  const confirmedTextFontSizeRef = useRef(DEFAULT_TEXT_FONT_SIZE);
  const pointUpdateQueueRef = useRef<Promise<void>>(Promise.resolve());
  const latestPointGeometryRef = useRef(new Map<string, FieldGeometry>());
  const latestFieldItemAnnotationRef = useRef(
    new Map<string, Doc<"fieldPilotItems">["annotation"]>()
  );
  const fieldItemAnnotationEditCountsRef = useRef(new Map<string, number>());
  const fieldItemAnnotationEditQueuesRef = useRef(new Map<string, Promise<void>>());

  const beginFieldItemAnnotationEdit = useCallback((key: string) => {
    fieldItemAnnotationEditCountsRef.current.set(
      key,
      (fieldItemAnnotationEditCountsRef.current.get(key) ?? 0) + 1
    );
  }, []);

  const endFieldItemAnnotationEdit = useCallback((key: string) => {
    const nextCount = (fieldItemAnnotationEditCountsRef.current.get(key) ?? 1) - 1;
    if (nextCount <= 0) {
      fieldItemAnnotationEditCountsRef.current.delete(key);
    } else {
      fieldItemAnnotationEditCountsRef.current.set(key, nextCount);
    }
  }, []);

  const enqueueFieldItemAnnotationEdit = useCallback(
    <T,>(itemId: Id<"fieldPilotItems">, operation: () => Promise<T>) => {
      const key = String(itemId);
      const previous = fieldItemAnnotationEditQueuesRef.current.get(key) ?? Promise.resolve();
      setPendingFieldEditCount((current) => current + 1);
      beginFieldItemAnnotationEdit(key);
      const result = previous.catch(() => undefined).then(operation);
      const tail = result.then(() => undefined, () => undefined);
      fieldItemAnnotationEditQueuesRef.current.set(key, tail);
      void tail.finally(() => {
        setPendingFieldEditCount((current) => Math.max(0, current - 1));
        endFieldItemAnnotationEdit(key);
        if (fieldItemAnnotationEditQueuesRef.current.get(key) === tail) {
          fieldItemAnnotationEditQueuesRef.current.delete(key);
        }
      });
      return result;
    },
    [beginFieldItemAnnotationEdit, endFieldItemAnnotationEdit]
  );

  useEffect(() => {
    pointUpdateQueueRef.current = Promise.resolve();
    latestPointGeometryRef.current.clear();
    latestFieldItemAnnotationRef.current.clear();
    fieldItemAnnotationEditCountsRef.current.clear();
    fieldItemAnnotationEditQueuesRef.current.clear();
  }, [routeJobId]);

  useEffect(() => {
    const handleResize = () => {
      const newHeight = window.innerHeight;
      setInnerHeight(newHeight);
      setPanelHeight((currentHeight) =>
        Math.min(currentHeight, getBottomPanelMaxHeight(newHeight))
      );
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleViewportChange = useCallback((viewport: DocumentViewportValue) => {
    viewportRef.current = viewport;
    const nextAspectRatio = viewport.activePageMetrics?.aspectRatio;
    const nextWidth = viewport.activePageMetrics?.width ?? 0;
    const nextHeight = viewport.activePageMetrics?.height ?? 0;
    const normalizedAspectRatio =
      Number.isFinite(nextAspectRatio) && (nextAspectRatio as number) > 0
        ? (nextAspectRatio as number)
        : undefined;
    setActivePagePixelMetrics((current) =>
      current.width === nextWidth && current.height === nextHeight
        ? current
        : { width: nextWidth, height: nextHeight }
    );
    setActivePageAspectRatio((current) =>
      current === normalizedAspectRatio ? current : normalizedAspectRatio
    );
  }, []);

  const { data: details } = useQuery(
    convexQuery(api.fieldPilot.jobs.getWorkspaceDetails, routeJobId ? { jobId: routeJobId } : "skip")
  );
  const exportFilenames = useExportFilenames(routeJobId ? { kind: "fieldPilotJob", id: routeJobId } : null);
  const fieldReportTarget = exportTarget("field_pilot_pdf", {}, {
    projectName: details?.project?.name,
    jobName: details?.job?.name,
  });
  const fieldReportName = exportFilenames.resolve(fieldReportTarget.key, fieldReportTarget.defaultName);
  const { data: viewerSources = [] } = useQuery(
    convexQuery(api.fieldPilot.jobs.listPlanDocumentViewerSources, routeJobId ? { jobId: routeJobId } : "skip")
  );
  const { data: progressRows = [] } = useQuery(
    convexQuery(api.fieldPilot.progress.getTagProgressByJob, routeJobId ? { jobId: routeJobId } : "skip")
  );
  const { data: scheduleRows = [] } = useQuery(
    convexQuery(api.fieldPilot.progress.getScheduleByJob, routeJobId ? { jobId: routeJobId, timeZone } : "skip")
  );
  const { data: subTasksList = [] } = useQuery(
    convexQuery(api.fieldPilot.subTasks.listByJob, routeJobId ? { jobId: routeJobId } : "skip")
  );
  const { data: pdfExport } = useQuery(
    convexQuery(api.fieldPilot.exports.getLatestPdfExport, routeJobId ? { jobId: routeJobId } : "skip")
  );
  const { data: pdfExportDownloadUrl } = useQuery(
    convexQuery(api.fieldPilot.exports.getPdfExportDownloadUrl, routeJobId ? { jobId: routeJobId } : "skip")
  );

  const setProgressMode = useMutation(api.fieldPilot.tags.setProgressMode);
  const setIncludedBatch = useMutation(api.fieldPilot.tags.setIncludedBatch);
  const createCustomTag = useMutation(api.fieldPilot.tags.createCustom);
  const createItem = useMutation(api.fieldPilot.items.create);
  const updateItem = useMutation(api.fieldPilot.items.update);
  const updateStatusMany = useMutation(api.fieldPilot.items.updateStatusMany);
  const updateTagMany = useMutation(api.fieldPilot.items.updateTagMany);
  const applyLeaderMany = useMutation(api.fieldPilot.items.applyLeaderMany);
  const applyStrokeWidthMany = useMutation(api.fieldPilot.items.applyStrokeWidthMany);
  const groupItemLabels = useMutation(api.fieldPilot.itemLabelGroups.groupLabels);
  const ungroupItemLabels = useMutation(api.fieldPilot.itemLabelGroups.ungroupLabels);
  const groupAllItemLabelsForScope = useMutation(
    api.fieldPilot.itemLabelGroups.groupAllLabelsForScope
  );
  const ungroupAllItemLabelsForScope = useMutation(
    api.fieldPilot.itemLabelGroups.ungroupAllLabelsForScope
  );
  const restoreAnnotationsMany = useMutation(api.fieldPilot.items.restoreAnnotationsMany);
  const restoreItemStates = useMutation(api.fieldPilot.items.restoreEditableStatesMany);
  const removeItems = useMutation(api.fieldPilot.items.removeMany);
  const restoreItems = useMutation(api.fieldPilot.items.restoreMany);
  const generatePdfExport = useMutation(api.fieldPilot.exports.generatePdfExport);
  const updatePlanDocumentTextSettings = useMutation(
    api.fieldPilot.textSettings.updatePlanDocument
  );
  const createStatus = useMutation(api.fieldPilot.statuses.create);
  const updateStatusDefinition = useMutation(api.fieldPilot.statuses.update);
  const deleteStatusDefinition = useMutation(api.fieldPilot.statuses.archive);
  const reorderStatuses = useMutation(api.fieldPilot.statuses.reorder);
  const addItemsToSubTask = useMutation(api.fieldPilot.subTasks.addItems);
  const removeItemsFromSubTask = useMutation(api.fieldPilot.subTasks.removeItems);
  const ensureSoloFromItem = useMutation(api.fieldPilot.subTasks.ensureSoloFromItem);
  const removeEmptySubTask = useMutation(api.fieldPilot.subTasks.deleteEmpty);
  const seedUnmeasuredTags = useMutation(api.fieldPilot.tags.seedUnmeasuredTags);
  const createTaskFromCanonicalTag = useMutation(fieldPilotDetectionsApi.createTaskFromCanonicalTag);
  const convertTagDetectionsToPoints = useMutation(fieldPilotDetectionsApi.convertTagDetectionsToPoints);
  const convex = useConvex();
  const history = useUndoRedoHistory(
    String(routeJobId ?? ""),
    50,
    (error) => {
      console.error(error);
      notifyError(error, "Failed to update edit history");
    },
    canEdit && isFieldHistoryEnabled(pendingFieldEditCount)
  );
  const didSeedTagsRef = useRef(false);

  const handleUndo = useCallback(async () => {
    try {
      await history.undo();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to undo the last change");
    }
  }, [history]);

  const handleRedo = useCallback(async () => {
    try {
      await history.redo();
    } catch (error) {
      console.error(error);
      notifyError(error, "Failed to redo the last change");
    }
  }, [history]);

  // Auto-seed unmeasured schedule/takeoff tags once per job so the tag picker always shows the
  // full universe of tags (replaces the old "Add all tags" button).
  useEffect(() => {
    didSeedTagsRef.current = false;
  }, [routeJobId]);
  useEffect(() => {
    if (!canEdit || !routeJobId || !details || didSeedTagsRef.current) return;
    didSeedTagsRef.current = true;
    seedUnmeasuredTags({ jobId: routeJobId }).catch(() => {
      // Non-blocking: the picker still works with the already-measured tags.
      didSeedTagsRef.current = false;
    });
  }, [canEdit, routeJobId, details, seedUnmeasuredTags]);

  // Reset the status filter if its selected status was deleted, otherwise the
  // grid would filter by an orphaned id and render empty with no matching option.
  useEffect(() => {
    if (statusFilterId === "all" || !details) return;
    const exists = details.statuses.some((status) => status._id === statusFilterId);
    if (!exists) setStatusFilterId("all");
  }, [statusFilterId, details]);

  const planSources = viewerSources.filter((source) => source.sourceType === "plan");
  const incompletePlanDocumentCount = planSources.filter(
    (source) => source.indexingStatus !== "completed"
  ).length;
  const activeViewerSource = resolveActiveViewerSource(planSources, activeSourceId);
  const pageLabels = useRevisionSheetLabels(activeViewerSource?.revisionId);
  const activePlanDocumentId = activeViewerSource?.planDocumentId as Id<"fieldPilotPlanDocuments"> | undefined;
  const { available: snapAvailable } = useSnapArtifactsAvailable(
    activeViewerSource?.revisionId as Id<"projectDocumentRevisions"> | undefined
  );
  useEffect(() => {
    if (!snapAvailable && snapEnabled) {
      setSnapEnabled(false);
    }
  }, [snapAvailable, snapEnabled]);

  const measurementScale = useMeasurementScale({
    projectId: activeViewerSource?.projectId as Id<"projects"> | undefined,
    documentId: activeViewerSource?.documentId as Id<"projectDocuments"> | undefined,
    revisionId: activeViewerSource?.revisionId as Id<"projectDocumentRevisions"> | undefined,
    pageNumber: activePage,
    pageAspectRatio: activePageAspectRatio,
  });
  useOnEditAccessLost(() => measurementScale.cancelCalibration());
  const measurementScaleValue = measurementScale.scale;
  const textSettingsKey = useMemo(
    () =>
      routeJobId
        ? `${FIELD_PILOT_TEXT_SETTINGS_KEY_PREFIX}:${routeJobId}:${activePlanDocumentId ?? "none"}`
        : null,
    [activePlanDocumentId, routeJobId]
  );
  useEffect(() => {
    activeTextSettingsKeyRef.current = textSettingsKey;
  }, [textSettingsKey]);
  const persistedTextLocked = activeViewerSource?.measurementTextLocked;
  const persistedTextFontSize = activeViewerSource?.measurementTextFontSize;

  const persistTextSettings = useCallback(
    (settings: {
      textLocked: boolean;
      textFontSize: number;
      labelsVisible: boolean;
    }) => {
      if (!textSettingsKey) return;
      try {
        localStorage.setItem(textSettingsKey, JSON.stringify(settings));
      } catch {
        // Keep FieldPilot usable even when browser storage is unavailable.
      }
    },
    [textSettingsKey]
  );

  const toggleTextLocked = useCallback(() => {
    const next = !textLocked;
    setTextLocked(next);
    persistTextSettings({ textLocked: next, textFontSize, labelsVisible });
    if (activePlanDocumentId && textSettingsKey) {
      const saveVersion = ++textLockSaveVersionRef.current;
      void updatePlanDocumentTextSettings({
        planDocumentId: activePlanDocumentId,
        textLocked: next,
      })
        .then(() => {
          if (activeTextSettingsKeyRef.current === textSettingsKey) {
            confirmedTextLockedRef.current = next;
          }
        })
        .catch((error) => {
          console.error(error);
          if (
            textLockSaveVersionRef.current !== saveVersion ||
            activeTextSettingsKeyRef.current !== textSettingsKey
          ) {
            return;
          }
          const confirmed = confirmedTextLockedRef.current;
          setTextLocked(confirmed);
          persistTextSettings({
            textLocked: confirmed,
            textFontSize,
            labelsVisible,
          });
          notifyError(error, "Failed to save annotation lock setting");
        });
    }
  }, [
    activePlanDocumentId,
    labelsVisible,
    persistTextSettings,
    textFontSize,
    textLocked,
    textSettingsKey,
    updatePlanDocumentTextSettings,
  ]);

  const setFieldTextFontSize = useCallback(
    (nextFontSize: number) => {
      if (!Number.isFinite(nextFontSize) || nextFontSize <= 0) return;
      setTextFontSize(nextFontSize);
      persistTextSettings({ textLocked, textFontSize: nextFontSize, labelsVisible });
      if (activePlanDocumentId && textSettingsKey) {
        const saveVersion = ++textFontSizeSaveVersionRef.current;
        void updatePlanDocumentTextSettings({
          planDocumentId: activePlanDocumentId,
          textFontSize: nextFontSize,
        })
          .then(() => {
            if (activeTextSettingsKeyRef.current === textSettingsKey) {
              confirmedTextFontSizeRef.current = nextFontSize;
            }
          })
          .catch((error) => {
            console.error(error);
            if (
              textFontSizeSaveVersionRef.current !== saveVersion ||
              activeTextSettingsKeyRef.current !== textSettingsKey
            ) {
              return;
            }
            const confirmed = confirmedTextFontSizeRef.current;
            setTextFontSize(confirmed);
            persistTextSettings({
              textLocked,
              textFontSize: confirmed,
              labelsVisible,
            });
            notifyError(error, "Failed to save annotation text size");
          });
      }
    },
    [
      activePlanDocumentId,
      labelsVisible,
      persistTextSettings,
      textLocked,
      textSettingsKey,
      updatePlanDocumentTextSettings,
    ]
  );

  const toggleLabelsVisible = useCallback(() => {
    setLabelsVisible((current) => {
      const next = !current;
      persistTextSettings({ textLocked, textFontSize, labelsVisible: next });
      return next;
    });
  }, [persistTextSettings, textFontSize, textLocked]);

  useEffect(() => {
    if (!textSettingsKey) {
      setTextLocked(false);
      setTextFontSize(DEFAULT_TEXT_FONT_SIZE);
      setLabelsVisible(true);
      setLoadedTextSettingsKey(null);
      return;
    }

    let storedSettings: Partial<{
      textLocked: boolean;
      textFontSize: number;
      labelsVisible: boolean;
    }> = {};
    try {
      const stored = localStorage.getItem(textSettingsKey);
      storedSettings = stored ? JSON.parse(stored) : {};
    } catch {
      // A browser-storage failure must not overwrite a durable drawing setting.
    }

    const nextTextLocked = persistedTextLocked ?? (storedSettings.textLocked === true);
    const nextTextFontSize =
      persistedTextFontSize !== undefined &&
      Number.isFinite(persistedTextFontSize) &&
      persistedTextFontSize > 0
        ? persistedTextFontSize
        : Number.isFinite(storedSettings.textFontSize) &&
            (storedSettings.textFontSize ?? 0) > 0
          ? storedSettings.textFontSize!
          : DEFAULT_TEXT_FONT_SIZE;
    setTextLocked(nextTextLocked);
    setTextFontSize(nextTextFontSize);
    setLabelsVisible(storedSettings.labelsVisible !== false);
    confirmedTextLockedRef.current = nextTextLocked;
    confirmedTextFontSizeRef.current = nextTextFontSize;
    setLoadedTextSettingsKey(textSettingsKey);
  }, [persistedTextFontSize, persistedTextLocked, textSettingsKey]);

  useEffect(() => {
    if (
      !canEdit ||
      !activePlanDocumentId ||
      !textSettingsKey ||
      loadedTextSettingsKey !== textSettingsKey ||
      textSettingsMigrationKeyRef.current === textSettingsKey ||
      (persistedTextLocked !== undefined && persistedTextFontSize !== undefined)
    ) {
      return;
    }

    textSettingsMigrationKeyRef.current = textSettingsKey;
    void updatePlanDocumentTextSettings({
      planDocumentId: activePlanDocumentId,
      textLocked,
      textFontSize,
    }).catch((error) => {
      console.error("Failed to migrate FieldPilot annotation text settings", error);
    });
  }, [
    canEdit,
    activePlanDocumentId,
    loadedTextSettingsKey,
    persistedTextFontSize,
    persistedTextLocked,
    textFontSize,
    textLocked,
    textSettingsKey,
    updatePlanDocumentTextSettings,
  ]);

  const scaleController = useScaleToolsetRailController({
    expanded: activeToolsetId === VIEWPORT_TOOLSET_IDS.scale,
    onExpandedChange: (expanded) =>
      setActiveToolsetId(expanded ? VIEWPORT_TOOLSET_IDS.scale : null),
    calibrationState: measurementScale.calibrationState,
    currentScale: measurementScaleValue,
    customDistanceInput: measurementScale.customDistanceInput,
    setCustomDistanceInput: measurementScale.setCustomDistanceInput,
    startCalibration: measurementScale.startCalibration,
    applyCustomDistance: measurementScale.applyCustomDistance,
    applyPresetScale: measurementScale.applyPresetScale,
    cancelCalibration: measurementScale.cancelCalibration,
    pagePixelWidth: activePagePixelMetrics.width,
    pagePixelHeight: activePagePixelMetrics.height,
  });

  const measurementController = useMeasurementToolsetRailController({
    scale: measurementScaleValue,
    expanded: activeToolsetId === VIEWPORT_TOOLSET_IDS.measurement,
    onExpandedChange: (expanded) => setActiveToolsetId(expanded ? VIEWPORT_TOOLSET_IDS.measurement : null),
    activeTool: isMeasurementTool(activeTool) ? activeTool : null,
    activeExtraToolId: activeTool === FIELD_POINT_TOOL_ID ? FIELD_POINT_TOOL_ID : null,
    extraTools: [
      {
        id: FIELD_POINT_TOOL_ID,
        label: "Point",
        icon: <Dot className="size-4" />,
      },
    ],
    activeMeasurementType,
    onToolChange: (tool) => {
      if (tool) enableMeasurements();
      setActiveTool(tool);
      if (tool) setActiveTagTool(null);
      setActiveToolsetId((current) => (tool ? VIEWPORT_TOOLSET_IDS.measurement : current));
      setDraftMeasurement(null);
      setSelectedItemIds(new Set());
    },
    onExtraToolSelect: (toolId) => {
      if (toolId) enableMeasurements();
      setActiveTool(toolId === FIELD_POINT_TOOL_ID ? FIELD_POINT_TOOL_ID : null);
      if (toolId) setActiveTagTool(null);
      setActiveToolsetId((current) => (toolId ? VIEWPORT_TOOLSET_IDS.measurement : current));
      setDraftMeasurement(null);
      setSelectedItemIds(new Set());
    },
    onMeasurementTypeChange: setActiveMeasurementType,
    snapEnabled,
    onSnapToggle: () => setSnapEnabled((current) => !current),
    snapAvailable,
    angleLockEnabled,
    onAngleLockToggle: () => setAngleLockEnabled((current) => !current),
    incrementSnapEnabled,
    onIncrementSnapToggle: () => setIncrementSnapEnabled((current) => !current),
    incrementStepFeet,
    onIncrementStepChange: setIncrementStepFeet,
    showHeightInput:
      activeMeasurementType === MEASUREMENT_TYPES.CF ||
      ((activeTool === "line" || activeTool === "polyline") && activeMeasurementType === MEASUREMENT_TYPES.SQFT),
    heightValue,
    onHeightChange: setHeightValue,
    textLocked,
    onTextLockToggle: () => toggleTextLocked(),
    textFontSize,
    onTextFontSizeChange: setFieldTextFontSize,
    labelsVisible,
    onLabelsVisibleToggle: () => toggleLabelsVisible(),
  });
  const headerToolsets = useMemo(
    () => getViewportToolsetsForPlacement(VIEWPORT_VIEW_IDS.fieldPilot, VIEWPORT_TOOLSET_PLACEMENTS.topToolbar),
    []
  );

  const { data: fieldItems = [] } = useQuery(
    convexQuery(
      api.fieldPilot.items.listByJobPage,
      routeJobId && activePlanDocumentId
        ? { jobId: routeJobId, planDocumentId: activePlanDocumentId, pageNumber: activePage }
        : "skip"
    )
  );
  const documentSource: DocumentSource | null = activeViewerSource?.fileUrl
    ? {
        revisionId: String(activeViewerSource.revisionId),
        fileUrl: activeViewerSource.fileUrl,
        filename: activeViewerSource.filename,
        mediaKind: getMediaKind(activeViewerSource.filename),
      }
    : null;

  const statusById = useMemo(() => {
    const map = new Map<string, { name: string; colorHex: string }>();
    for (const status of details?.statuses ?? []) {
      map.set(String(status._id), status);
    }
    return map;
  }, [details?.statuses]);

  const measurementTypeByTagId = useMemo(
    () => measurementTypeByTagIdFromItems(fieldItems),
    [fieldItems]
  );

  const tagSections = useMemo<MultiSelectSection[]>(() => {
    type FieldTag = NonNullable<typeof details>["tags"][number];
    const groups = new Map<
      string,
      { name: string; pageNumber: number | null; tags: FieldTag[] }
    >();
    for (const tag of details?.tags ?? []) {
      const key = tag.sourceTableId ?? "__other__";
      const group = groups.get(key) ?? {
        name: tag.sourceTableName ?? "Other",
        pageNumber: tag.sourceTablePageNumber ?? null,
        tags: [],
      };
      group.tags.push(tag);
      groups.set(key, group);
    }
    return Array.from(groups.entries())
      .sort(([, a], [, b]) => {
        // Group with no table sorts last; otherwise order by table page then name.
        if ((a.pageNumber ?? Infinity) !== (b.pageNumber ?? Infinity)) {
          return (a.pageNumber ?? Infinity) - (b.pageNumber ?? Infinity);
        }
        return a.name.localeCompare(b.name);
      })
      .map(([key, group]) => ({
        id: `table:${key}`,
        label: group.name,
        description: group.pageNumber ? `Page ${group.pageNumber}` : "Included in drawings and progress",
        options: group.tags
          .slice()
          .sort(compareTagsByRawValue)
          .map((tag) => ({
            value: tag._id,
            label: resolveTagLabel(tag),
            adornment:
              tag.measuredCount > 0 ? (
                <span
                  className="inline-flex"
                  title={`${tag.measuredCount} marked measurement${tag.measuredCount === 1 ? "" : "s"}`}
                >
                  <CountBadge
                    value={tag.measuredCount}
                    label={`marked measurement${tag.measuredCount === 1 ? "" : "s"}`}
                  />
                </span>
              ) : undefined,
          })),
      }));
  }, [details?.tags]);
  const selectedTagIds = useMemo(
    () => {
      const baseIds = new Set((details?.tags ?? []).filter((tag) => tag.included).map((tag) => tag._id));
      for (const [tagId, included] of pendingSelections) {
        if (included) {
          baseIds.add(tagId as Id<"fieldPilotTags">);
        } else {
          baseIds.delete(tagId as Id<"fieldPilotTags">);
        }
      }
      return Array.from(baseIds);
    },
    [details?.tags, pendingSelections]
  );
  const { data: pageTagDetectionResult } = useQuery(
    convexQuery(
      fieldPilotDetectionsApi.listByJobPage,
      routeJobId &&
        activePlanDocumentId &&
        layerVisibility.tags &&
        selectedTagIds.length > 0
        ? {
            jobId: routeJobId,
            planDocumentId: activePlanDocumentId,
            pageNumber: activePage,
            tagIds: selectedTagIds,
          }
        : "skip"
    )
  );
  const pageTagDetections = pageTagDetectionResult?.activeDetections ?? [];
  const archivedPageTagDetections = pageTagDetectionResult?.archivedDetections ?? [];
  const locationQueryTagIds = useMemo(() => {
    const ids = new Set<Id<"fieldPilotTags">>(selectedTagIds as Id<"fieldPilotTags">[]);
    for (const tagId of highlightedTagIds) {
      ids.add(tagId);
    }
    return Array.from(ids);
  }, [highlightedTagIds, selectedTagIds]);
  const { data: tagLocationsResult } = useQuery(
    convexQuery(
      fieldPilotDetectionsApi.listLocationsByTags,
      routeJobId && locationQueryTagIds.length > 0
        ? { jobId: routeJobId, tagIds: locationQueryTagIds }
        : "skip"
    )
  );
  const tagLocations = tagLocationsResult?.locations ?? [];
  const tagLocationsByTag = tagLocationsResult?.byTag ?? [];
  const tagLocationsTruncated = tagLocationsResult?.isTruncated ?? false;
  const tagLocationsTotalCount = tagLocationsResult?.totalCount;
  const selectedTagIdSet = useMemo(() => new Set(selectedTagIds.map(String)), [selectedTagIds]);
  const activeTags = useMemo(
    () =>
      (details?.tags ?? [])
        .filter((tag) => selectedTagIdSet.has(String(tag._id)))
        .sort(compareTagsByRawValue),
    [details?.tags, selectedTagIdSet]
  );
  // Deterministic tag → palette color, indexed by displayOrder (mirrors the export palette).
  const tagColorById = useMemo(
    () => buildTagColorMap(details?.tags ?? []),
    [details?.tags]
  );
  const railTagOptions = useMemo(
    () => activeTags.map((tag) => ({ id: String(tag._id), label: resolveTagLabel(tag) })),
    [activeTags]
  );
  const tagPlacementOptions = useMemo<DocumentTagOption[]>(
    () =>
      activeTags.map((tag) => {
        const matchingDetection = [...pageTagDetections, ...archivedPageTagDetections].find(
          (detection) => String(detection.fieldPilotTagId) === String(tag._id)
        );
        return {
          id: String(tag._id),
          label: resolveTagLabel(tag),
          rawText: tag.rawValue,
          normalizedText: tag.normalizedValue,
          color: tagColorById.get(String(tag._id)),
          ...(matchingDetection
            ? {
                classification: matchingDetection.classification,
                bbox: matchingDetection.bbox,
              }
            : {}),
        };
      }),
    [activeTags, archivedPageTagDetections, pageTagDetections, tagColorById]
  );
  useEffect(() => {
    if (railTagOptions.length === 0) {
      setSelectedAddTagOptionId(null);
      return;
    }
    if (
      selectedAddTagOptionId &&
      railTagOptions.some((option) => option.id === selectedAddTagOptionId)
    ) {
      return;
    }
    setSelectedAddTagOptionId(railTagOptions[0].id);
  }, [railTagOptions, selectedAddTagOptionId]);
  const handleTagsToolSelect = useCallback((toolId: TagToolId | null) => {
    setActiveTagTool(toolId);
    if (toolId) {
      setActiveTool(null);
      setDraftMeasurement(null);
      setSelectedItemIds(new Set());
      setActiveToolsetId(VIEWPORT_TOOLSET_IDS.tags);
    }
  }, []);
  const tagsController = useTagsToolsetRailController({
    expanded: activeToolsetId === VIEWPORT_TOOLSET_IDS.tags,
    onExpandedChange: (expanded) => {
      if (expanded) {
        enableTags();
        setActiveToolsetId(VIEWPORT_TOOLSET_IDS.tags);
      } else {
        setActiveToolsetId(null);
      }
    },
    activeTool: activeTagTool,
    disabled: !layerVisibility.tags,
    tagOptions: railTagOptions,
    selectedTagOptionId: selectedAddTagOptionId,
    onSelectedTagOptionChange: setSelectedAddTagOptionId,
    onToolSelect: handleTagsToolSelect,
  });
  const { enabledToolsets: railToolsets, controllers: railControllers } = useViewportToolsetControllers(
    VIEWPORT_VIEW_IDS.fieldPilot,
    {
      [VIEWPORT_TOOLSET_IDS.scale]: scaleController,
      [VIEWPORT_TOOLSET_IDS.measurement]: measurementController,
      [VIEWPORT_TOOLSET_IDS.tags]: tagsController,
    }
  );
  const visibleFieldItems = useMemo(
    () =>
      fieldItems.filter(
        (item) =>
          selectedTagIdSet.has(String(item.tagId)) &&
          (statusFilterId === "all" || String(item.statusId) === statusFilterId)
      ),
    [fieldItems, selectedTagIdSet, statusFilterId]
  );
  useEffect(() => {
    for (const item of visibleFieldItems) {
      const key = String(item._id);
      if ((fieldItemAnnotationEditCountsRef.current.get(key) ?? 0) === 0) {
        latestFieldItemAnnotationRef.current.set(key, item.annotation);
      }
    }
  }, [visibleFieldItems]);
  const visibleMeasurementItems = useMemo(
    () => visibleFieldItems.filter((item) => item.geometry.type !== "point"),
    [visibleFieldItems]
  );
  const measurementRecords = useMemo<MeasurementOverlayRecord[]>(
    () =>
      visibleMeasurementItems.flatMap((item) => {
        const geometry = toOverlayGeometry(item.geometry);
        if (!geometry) return [];
        return [
          {
            _id: item._id as unknown as Id<"takeoffPilotMeasurements">,
            // The overlay keys color/label maps by `tagId`; FieldPilot drives them per-measurement
            // (label = sub-task/tag name, color = status or tag) so we key by the item's own id.
            tagId: item._id as unknown as Id<"takeoffPilotExtractedTags">,
            tool: (item.geometryKind === "bbox" ? "rectangle" : item.geometryKind) as ToolType,
            measurementType: item.measurementType ?? MEASUREMENT_TYPES.LF,
            geometry,
            annotation: item.annotation as MeasurementAnnotation | undefined,
            height: item.height,
            value: item.quantityValue ?? 0,
            labelGroupId: item.labelGroupId,
            labelGroupScopeKey: `${String(item.tagId)}:${item.subTaskId ? String(item.subTaskId) : ""}`,
          },
        ];
      }),
    [visibleMeasurementItems]
  );
  const selectedMeasurementIds = useMemo(
    () =>
      measurementRecords
        .filter((measurement) => selectedItemIds.has(String(measurement._id)))
        .map((measurement) => measurement._id),
    [measurementRecords, selectedItemIds]
  );
  const previewFieldStrokeWidth = useCallback((
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number | null
  ) => {
    return fieldMeasurementOverlayRef.current?.previewStrokeWidth(measurementIds, strokeWidth);
  }, []);
  const selectedMeasurements = useMemo(
    () => measurementRecords.filter((measurement) => selectedItemIds.has(String(measurement._id))),
    [measurementRecords, selectedItemIds]
  );
  const activeMeasurementId = selectedMeasurementIds[0] ?? null;
  const tagRawValueById = useMemo(() => {
    const map = new Map<string, string>();
    for (const tag of details?.tags ?? []) {
      map.set(String(tag._id), tag.rawValue);
    }
    return map;
  }, [details?.tags]);
  const statusOptions = useMemo(
    () =>
      (details?.statuses ?? []).map((status) => ({
        id: status._id as unknown as Id<"takeoffPilotExtractedTags">,
        label: status.name,
      })),
    [details?.statuses]
  );
  const visibleFieldItemIds = useMemo(
    () => new Set(visibleFieldItems.map((item) => String(item._id))),
    [visibleFieldItems]
  );
  const defaultStatus =
    (details?.statuses ?? []).find((status) => status.isDefault === true) ??
    (details?.statuses ?? []).find((status) => status.name === "Not Installed") ??
    details?.statuses[0];
  const defaultStatusName = defaultStatus?.name ?? "Not Installed";
  const effectiveSelectedTagId = selectedTagId && selectedTagIdSet.has(selectedTagId) ? selectedTagId : activeTags[0]?._id || "";
  const effectiveSelectedStatusId = selectedStatusId || defaultStatus?._id || "";
  const selectedItems = visibleFieldItems.filter((item) => selectedItemIds.has(String(item._id)));
  const activeSelectedItem = selectedItems[0] ?? null;
  const visibleProgressRows = useMemo(
    () =>
      progressRows
        .filter((row) => selectedTagIdSet.has(String(row.tagId)))
        .sort(compareTagsByRawValue),
    [progressRows, selectedTagIdSet]
  );
  const scheduleByTagId = useMemo(() => {
    const map = new Map<string, FieldPilotScheduleRowWithSubTasks>();
    for (const row of scheduleRows as FieldPilotScheduleRowWithSubTasks[]) {
      // Refresh time-dependent fields against the client clock (see withFreshScheduleTiming),
      // including each nested sub-task and the ungrouped remainder.
      map.set(String(row.tagId), {
        ...withFreshScheduleTiming(row, scheduleNow),
        subTasks: (row.subTasks ?? []).map((subTask) => withFreshScheduleTiming(subTask, scheduleNow)),
        ungrouped: row.ungrouped ? withFreshScheduleTiming(row.ungrouped, scheduleNow) : row.ungrouped,
      });
    }
    return map;
  }, [scheduleRows, scheduleNow]);
  const subTaskNameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const subTask of subTasksList) {
      map.set(String(subTask._id), subTask.name);
    }
    return map;
  }, [subTasksList]);
  // Distinct parent tags in the current selection; a sub-task belongs to a single tag, so a
  // selection spanning more than one tag cannot be assigned to (or grouped into) a sub-task.
  const selectionTagIds = useMemo(
    () => Array.from(new Set(selectedItems.map((item) => String(item.tagId)))),
    [selectedItems]
  );
  const { value: tagSelectValue, isMixed: selectionSpansMultipleTags } =
    sharedSelectValue(selectionTagIds);
  const tagPlaceholder = selectionSpansMultipleTags ? "Multiple" : "Tag";
  // Existing sub-tasks under the selection's single parent tag, for the quick-assign dropdown.
  const selectionSubTasks = useMemo(() => {
    if (selectionTagIds.length !== 1) return [];
    const tagId = selectionTagIds[0];
    return subTasksList
      .filter((subTask) => String(subTask.tagId) === tagId)
      .map((subTask) => ({ subTaskId: subTask._id, name: subTask.name }));
  }, [subTasksList, selectionTagIds]);
  const selectedSubTaskIds = useMemo(
    () => new Set(selectedItems.map((item) => (item.subTaskId ? String(item.subTaskId) : ""))),
    [selectedItems]
  );
  const selectedSubTaskId =
    selectedSubTaskIds.size === 1 ? (Array.from(selectedSubTaskIds)[0] || null) : null;
  const selectionIsAllUngrouped =
    selectedItems.length > 0 && selectedItems.every((item) => !item.subTaskId);
  const subTaskSelectValue = selectedSubTaskId
    ? selectedSubTaskId
    : selectionIsAllUngrouped
      ? NONE_SUB_TASK_OPTION
      : "";
  const subTaskPlaceholder = selectedSubTaskIds.size > 1 ? "Multiple sub-tasks" : "Assign to sub-task…";
  const { value: statusSelectValue, isMixed: statusesAreMixed } = sharedSelectValue(
    selectedItems.map((item) => String(item.statusId))
  );
  const statusPlaceholder = statusesAreMixed ? "Multiple" : "Status";

  const activeTagsForNav = useMemo(
    () =>
      activeTags.map((tag) => ({
        tagId: tag._id,
        normalizedValue: tag.normalizedValue,
        rawValue: tag.rawValue,
      })),
    [activeTags]
  );
  const normalizedValueByTagId = useMemo(() => {
    const map = new Map<string, string>();
    for (const tag of details?.tags ?? []) {
      map.set(String(tag._id), tag.normalizedValue);
    }
    return map;
  }, [details?.tags]);
  const navigationDetections = useMemo<FieldPilotNavDetection[]>(() => {
    return tagLocations.flatMap((location) => {
      const normalizedText = normalizedValueByTagId.get(String(location.fieldPilotTagId));
      if (!normalizedText) return [];
      return [
        {
          _id: location.detectionId,
          normalizedText,
          pageNumber: location.pageNumber,
          documentOrder: location.documentOrder,
          bbox: location.bbox,
          planDocumentId: location.planDocumentId,
          planDocumentName: location.planDocumentName,
        },
      ];
    });
  }, [normalizedValueByTagId, tagLocations]);
  const planSourceByPlanDocumentId = useMemo(() => {
    const map = new Map<string, (typeof planSources)[number]>();
    for (const source of planSources) {
      map.set(String(source.planDocumentId), source);
    }
    return map;
  }, [planSources]);
  const layerCounts = useMemo(
    () => ({
      measurements: visibleFieldItems.length,
      tags: pageTagDetections.length,
    }),
    [pageTagDetections.length, visibleFieldItems.length]
  );

  const focusDetection = useCallback((detection: FieldPilotNavDetection) => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const contentCenter = getViewportContentCenter(
      viewport.workspaceSize,
      viewport.viewportPadding
    );
    const pageBounds = viewport.transforms.pageRectToScreenRect({
      x: 0,
      y: 0,
      width: 1,
      height: 1,
    });
    const center = {
      x: detection.bbox.x + detection.bbox.width / 2,
      y: detection.bbox.y + detection.bbox.height / 2,
    };
    const baseWidth = pageBounds.width / viewport.camera.zoom;
    const baseHeight = pageBounds.height / viewport.camera.zoom;
    viewport.requestZoomAt(MAX_VIEWPORT_ZOOM, contentCenter);
    viewport.setPan({
      x: baseWidth * MAX_VIEWPORT_ZOOM * (0.5 - center.x),
      y: baseHeight * MAX_VIEWPORT_ZOOM * (0.5 - center.y),
    });
  }, []);

  const {
    focusNextInstanceForTag,
    focusPreviousInstanceForTag,
    highlightedDetectionId,
    navigableTagIds,
  } = useTagInstanceNavigation({
    tags: activeTagsForNav,
    activeDetections: navigationDetections,
    enabled: layerVisibility.tags && activeTags.length > 0,
    navigationScopeKey: `${String(routeJobId ?? "none")}:${selectedTagIds.map(String).join(",")}`,
    compareDetections: comparePlanSetTagDetections,
    onFocusDetection: (detection) => {
      const isCurrentPlan =
        String(activePlanDocumentId ?? "") === String(detection.planDocumentId);
      if (!isCurrentPlan) {
        const source = planSourceByPlanDocumentId.get(String(detection.planDocumentId));
        if (source) {
          preservePendingDetectionFocusRef.current = true;
          pendingDetectionFocusRef.current = detection;
          setActiveSourceId(source.sourceId);
          setActivePage(detection.pageNumber);
          return;
        }
      }
      if (detection.pageNumber !== activePage) {
        pendingDetectionFocusRef.current = detection;
        viewportRef.current?.goToPage(detection.pageNumber);
        return;
      }
      pendingDetectionFocusRef.current = null;
      focusDetection(detection);
    },
  });

  useEffect(() => {
    if (!layerVisibility.tags) {
      setSelectedTagDetectionIds([]);
      setActiveTagTool(null);
      setActiveToolsetId((current) =>
        current === VIEWPORT_TOOLSET_IDS.tags ? null : current
      );
    }
  }, [layerVisibility.tags]);

  useEffect(() => {
    // Preserve pending focus across programmatic drawing switches from tag navigation.
    if (preservePendingDetectionFocusRef.current) {
      preservePendingDetectionFocusRef.current = false;
      return;
    }
    pendingDetectionFocusRef.current = null;
  }, [activePlanDocumentId]);

  useEffect(() => {
    const pendingDetection = pendingDetectionFocusRef.current;
    if (!pendingDetection) return;
    if (String(activePlanDocumentId ?? "") !== String(pendingDetection.planDocumentId)) {
      return;
    }
    if (pendingDetection.pageNumber !== activePage) {
      viewportRef.current?.goToPage(pendingDetection.pageNumber);
      return;
    }
    focusDetection(pendingDetection);
    pendingDetectionFocusRef.current = null;
  }, [activePage, activePlanDocumentId, focusDetection]);

  const scrollTagRowIntoView = useCallback((tagId: Id<"fieldPilotTags">) => {
    requestAnimationFrame(() => {
      progressRowRefs.current.get(String(tagId))?.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
      });
    });
  }, []);

  const highlightAndFocusTag = useCallback(
    (tagId: Id<"fieldPilotTags">) => {
      setHighlightedTagIds([tagId]);
      enableTags();
      setPendingSelections((previous) => {
        const next = new Map(previous);
        next.set(String(tagId), true);
        return next;
      });
      scrollTagRowIntoView(tagId);
      const locationsForTag = tagLocations.filter(
        (location) => String(location.fieldPilotTagId) === String(tagId)
      );
      const first = locationsForTag[0];
      if (!first) return;
      const normalizedText = normalizedValueByTagId.get(String(tagId));
      if (!normalizedText) return;
      const detection: FieldPilotNavDetection = {
        _id: first.detectionId,
        normalizedText,
        pageNumber: first.pageNumber,
        documentOrder: first.documentOrder,
        bbox: first.bbox,
        planDocumentId: first.planDocumentId,
        planDocumentName: first.planDocumentName,
      };
      const source = planSourceByPlanDocumentId.get(String(first.planDocumentId));
      if (source && String(activePlanDocumentId ?? "") !== String(first.planDocumentId)) {
        preservePendingDetectionFocusRef.current = true;
        pendingDetectionFocusRef.current = detection;
        setActiveSourceId(source.sourceId);
        setActivePage(first.pageNumber);
        return;
      }
      if (first.pageNumber !== activePage) {
        pendingDetectionFocusRef.current = detection;
        viewportRef.current?.goToPage(first.pageNumber);
        return;
      }
      focusDetection(detection);
    },
    [
      activePage,
      activePlanDocumentId,
      enableTags,
      focusDetection,
      normalizedValueByTagId,
      planSourceByPlanDocumentId,
      scrollTagRowIntoView,
      tagLocations,
    ]
  );

  const handleSearchDocumentTags = useCallback(
    async (query: string) => {
      if (!routeJobId) {
        return { status: "no_match" as const, matches: [] };
      }
      const response = await convex.query(fieldPilotDetectionsApi.searchPlanTags, {
        jobId: routeJobId,
        query,
      });
      return mapFieldPilotSearchToDialogResponse(response);
    },
    [convex, routeJobId]
  );

  const convertDialogTag = (details?.tags ?? []).find(
    (tag) => convertDialogTagId && String(tag._id) === String(convertDialogTagId)
  );
  const scheduleEditTag = (details?.tags ?? []).find(
    (tag) => scheduleEditTagId && String(tag._id) === String(scheduleEditTagId)
  );
  const scheduleEditRow = scheduleEditTagId
    ? scheduleByTagId.get(String(scheduleEditTagId))
    : undefined;
  const convertDialogSummary = convertDialogTagId
    ? summarizeLocationsForTag(tagLocations, convertDialogTagId, {
        isTruncated: tagLocationsTruncated,
        totalCount: tagLocationsTotalCount,
        byTag: tagLocationsByTag,
      })
    : null;

  const recordCreatedPointsHistory = (createdItemIds: Id<"fieldPilotItems">[]) => {
    if (!routeJobId || createdItemIds.length === 0) return;
    history.record({
      undo: async () => {
        await removeItems({ jobId: routeJobId, itemIds: createdItemIds });
      },
      redo: async () => {
        await restoreItems({ jobId: routeJobId, itemIds: createdItemIds });
      },
    });
  };

  const handleConvertSingleDetection = async (
    detection: (typeof pageTagDetections)[number]
  ) => {
    if (
      !routeJobId ||
      detection.alreadyConverted ||
      detection.archivedAt !== undefined ||
      convertingDetectionId !== null
    ) {
      return;
    }
    setConvertingDetectionId(String(detection._id));
    try {
      const result = await convertTagDetectionsToPoints({
        jobId: routeJobId,
        tagId: detection.fieldPilotTagId,
        detectionIds: [detection._id],
      });
      recordCreatedPointsHistory(result.createdItemIds);
      enableMeasurements();
      if (result.createdItemIds.length > 0) {
        toast.success("Point created");
      } else if (result.skippedExistingItemIds.length > 0) {
        toast.success("Point already existed");
      } else {
        toast.error("Tag is no longer available to convert");
      }
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to create point");
    } finally {
      setConvertingDetectionId(null);
    }
  };

  const assignSelectionToSubTask = (subTaskId: Id<"fieldPilotSubTasks">) => {
    if (selectedItems.length === 0) return;
    addItemsToSubTask({
      subTaskId,
      itemIds: selectedItems.map((item) => item._id),
    })
      .then(() =>
        toast.success(
          `Added ${selectedItems.length} item${selectedItems.length === 1 ? "" : "s"} to sub-task`
        )
      )
      .catch((error) =>
        notifyError(error, getUserFacingErrorMessage(error, "Failed to add to sub-task"))
      );
  };
  const clearSelectionSubTask = () => {
    if (!routeJobId || selectedItems.length === 0) return;
    removeItemsFromSubTask({
      jobId: routeJobId,
      itemIds: selectedItems.map((item) => item._id),
    })
      .then(() =>
        toast.success(
          `Removed ${selectedItems.length} item${selectedItems.length === 1 ? "" : "s"} from sub-task`
        )
      )
      .catch((error) =>
        notifyError(error, getUserFacingErrorMessage(error, "Failed to remove from sub-task"))
      );
  };
  // Per-measurement marker color: status color by default, tag palette color when colorizeBy="tag".
  const measurementColorById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of visibleFieldItems) {
      const color =
        colorizeBy === "tag"
          ? tagColorById.get(String(item.tagId)) ?? FIELD_PILOT_TAG_FALLBACK_COLOR
          : statusById.get(String(item.statusId))?.colorHex ?? FIELD_PILOT_TAG_FALLBACK_COLOR;
      map.set(String(item._id), color);
    }
    return map;
  }, [visibleFieldItems, colorizeBy, tagColorById, statusById]);
  // Per-item label: sub-task name when grouped, else the tag's display/raw name.
  const measurementLabelById = useMemo(() => {
    const map = new Map<string, string>();
    for (const item of visibleFieldItems) {
      const tagName =
        scheduleByTagId.get(String(item.tagId))?.displayName ??
        tagRawValueById.get(String(item.tagId)) ??
        item.geometryKind;
      map.set(
        String(item._id),
        resolveItemLabel({ subTaskId: item.subTaskId, subTaskNameById, tagName })
      );
    }
    return map;
  }, [visibleFieldItems, scheduleByTagId, tagRawValueById, subTaskNameById]);
  // sub-task id → its pipeline status line (rank + due dates), sourced from the schedule rollup.
  const subTaskStatusLineById = useMemo(() => {
    const map = new Map<string, FieldPilotScheduleRowWithSubTasks["subTasks"][number]["statusLine"]>();
    for (const row of scheduleByTagId.values()) {
      for (const subTask of row.subTasks ?? []) {
        map.set(String(subTask.subTaskId), subTask.statusLine ?? []);
      }
    }
    return map;
  }, [scheduleByTagId]);
  // tag id → its top-level finish date, used to anchor the "next" annotation's final-status date.
  const finishDateKeyByTagId = useMemo(() => {
    const map = new Map<string, string | null>();
    for (const row of scheduleRows as FieldPilotScheduleRowWithSubTasks[]) {
      map.set(String(row.tagId), row.finishDateKey);
    }
    return map;
  }, [scheduleRows]);
  // Per-item annotation line(s) (off/next/current/all), mirrored client-side from the export rule.
  const measurementAnnotationById = useMemo(() => {
    const map = new Map<string, string>();
    if (viewportAnnotation === "off") return map;
    const statuses = details?.statuses ?? [];
    for (const item of visibleFieldItems) {
      const statusLine = item.subTaskId
        ? subTaskStatusLineById.get(String(item.subTaskId))
        : undefined;
      const annotation = resolveItemAnnotation({
        mode: viewportAnnotation,
        statusId: item.statusId,
        statuses,
        statusLine,
        taskFinishDateKey: finishDateKeyByTagId.get(String(item.tagId)),
        formatDate: formatScheduleDate,
      });
      if (annotation) {
        map.set(String(item._id), annotation);
      }
    }
    return map;
  }, [
    viewportAnnotation,
    visibleFieldItems,
    subTaskStatusLineById,
    finishDateKeyByTagId,
    details?.statuses,
  ]);
  useEffect(() => {
    if (!routeJobId) {
      setShowScheduleColumns(false);
      return;
    }
    try {
      const stored = localStorage.getItem(
        `${FIELD_PILOT_SCHEDULE_COLUMNS_KEY_PREFIX}:${routeJobId}`
      );
      setShowScheduleColumns(stored === "true");
    } catch {
      setShowScheduleColumns(false);
    }
  }, [routeJobId]);
  // Status-grouped inversion of getTagProgressByJob: rows = statuses, each listing the tags
  // that have value in that status. Pure client-side; reuses the already-fetched progress data.
  const scheduleSummary = useMemo(() => {
    let behind = 0;
    let onTrack = 0;
    for (const [tagId, row] of scheduleByTagId) {
      if (!selectedTagIdSet.has(tagId) || !row.hasSchedule) continue;
      if (row.isBehind) behind += 1;
      else onTrack += 1;
    }
    return { behind, onTrack, scheduled: behind + onTrack };
  }, [scheduleByTagId, selectedTagIdSet]);

  useEffect(() => {
    setSelectedItemIds((current) => {
      let changed = false;
      const next = new Set<string>();
      for (const itemId of current) {
        if (visibleFieldItemIds.has(itemId)) {
          next.add(itemId);
        } else {
          changed = true;
        }
      }
      return changed ? next : current;
    });
  }, [visibleFieldItemIds]);

  const handleDropdownSelect = (tagId: string) => {
    setPendingSelections((previous) => {
      const next = new Map(previous);
      next.set(tagId, true);
      return next;
    });
  };

  const handleDropdownRemove = (tagId: string) => {
    setPendingSelections((previous) => {
      const next = new Map(previous);
      next.set(tagId, false);
      return next;
    });
  };

  const flushPendingSelections = useCallback(async () => {
    if (flushInFlightRef.current) {
      return await flushInFlightRef.current;
    }

    if (pendingSelections.size === 0) {
      return true;
    }

    const sentSelections = new Map(pendingSelections);
    const selections = Array.from(sentSelections.entries()).map(([tagId, included]) => ({
      tagId: tagId as Id<"fieldPilotTags">,
      included,
    }));

    let flushPromise: Promise<boolean> | null = null;
    flushPromise = (async () => {
      try {
        await setIncludedBatch({ selections });
        setPendingSelections((previous) => {
          const next = new Map(previous);
          for (const [id, included] of sentSelections) {
            if (next.get(id) === included) {
              next.delete(id);
            }
          }
          return next;
        });
        return true;
      } catch (error) {
        notifyError(error, error instanceof Error ? error.message : "Failed to update tag selections");
        return false;
      } finally {
        if (flushInFlightRef.current === flushPromise) {
          flushInFlightRef.current = null;
        }
      }
    })();

    flushInFlightRef.current = flushPromise;
    return await flushPromise;
  }, [pendingSelections, setIncludedBatch]);

  const handleDropdownOpenChange = useCallback(
    async (open: boolean) => {
      if (!open) {
        return await flushPendingSelections();
      }
      return true;
    },
    [flushPendingSelections]
  );

  const handleCreateFromDocumentTags = useCallback(
    async (canonicalTagIds: Id<"documentIntelligenceCanonicalTags">[]) => {
      if (!routeJobId || canonicalTagIds.length === 0) return;
      const createdIds: Id<"fieldPilotTags">[] = [];
      const failures: string[] = [];
      for (const canonicalTagId of canonicalTagIds) {
        try {
          const tagId = await createTaskFromCanonicalTag({
            jobId: routeJobId,
            canonicalTagId,
          });
          createdIds.push(tagId);
        } catch (error) {
          if (getOrganizationAccessError(error)) throw error;
          failures.push(
            error instanceof Error ? error.message : "Failed to add tag"
          );
        }
      }

      const primaryTagId = createdIds[0];
      if (primaryTagId) {
        setHighlightedTagIds(createdIds);
        enableTags();
        const createdSelections = createdIds.map((tagId) => ({
          tagId,
          included: true,
        }));
        setPendingSelections((previous) => {
          const next = new Map(previous);
          for (const tagId of createdIds) {
            next.set(String(tagId), true);
          }
          return next;
        });
        // Persist created selections directly — flushPendingSelections would
        // still see the pre-update pendingSelections snapshot.
        await setIncludedBatch({ selections: createdSelections });
        setPendingSelections((previous) => {
          const next = new Map(previous);
          for (const tagId of createdIds) {
            if (next.get(String(tagId)) === true) {
              next.delete(String(tagId));
            }
          }
          return next;
        });
        scrollTagRowIntoView(primaryTagId);
      }

      if (createdIds.length > 0 && failures.length === 0) {
        toast.success(
          createdIds.length === 1
            ? "Added 1 FieldPilot task from tag"
            : `Added ${createdIds.length} FieldPilot tasks from tags`
        );
        return;
      }
      if (createdIds.length > 0 && failures.length > 0) {
        toast.warning(
          `Added ${createdIds.length} of ${canonicalTagIds.length} tags. ${failures[0]}`
        );
        return;
      }
      toast.error(failures[0] ?? "Failed to add tags");
      throw new Error(failures[0] ?? "Failed to add tags");
    },
    [
      createTaskFromCanonicalTag,
      enableTags,
      routeJobId,
      scrollTagRowIntoView,
      setIncludedBatch,
    ]
  );

  const tagsForSection = (sectionId: string) => {
    const tableKey = sectionId.replace("table:", "");
    return (details?.tags ?? []).filter(
      (tag) => (tag.sourceTableId ?? "__other__") === tableKey
    );
  };

  const handleSelectAllTags = (sectionId: string) => {
    for (const tag of tagsForSection(sectionId)) {
      handleDropdownSelect(tag._id);
    }
  };

  const handleDeselectAllTags = (sectionId: string) => {
    for (const tag of tagsForSection(sectionId)) {
      handleDropdownRemove(tag._id);
    }
  };

  const handleItemPointerDown = (event: React.PointerEvent, itemId: Id<"fieldPilotItems">) => {
    event.stopPropagation();
    setSelectedTagDetectionIds([]);
    setSelectedItemIds((current) => {
      const next = new Set(event.shiftKey ? current : []);
      const id = String(itemId);
      if (event.shiftKey && next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectMeasurement = (
    measurementId: Id<"takeoffPilotMeasurements"> | null,
    options?: { shiftKey?: boolean }
  ) => {
    if (!measurementId) {
      setSelectedItemIds(new Set());
      return;
    }
    setSelectedTagDetectionIds([]);
    setSelectedItemIds((current) => {
      const next = new Set(options?.shiftKey ? current : []);
      const id = String(measurementId);
      if (options?.shiftKey && next.has(id)) {
        next.delete(id);
      } else {
        next.add(id);
      }
      return next;
    });
  };

  const handleSelectMeasurements = (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    options?: { additive?: boolean }
  ) => {
    if (measurementIds.length > 0) {
      setSelectedTagDetectionIds([]);
    }
    setSelectedItemIds((current) => {
      const next = new Set(options?.additive ? current : []);
      for (const measurementId of measurementIds) {
        next.add(String(measurementId));
      }
      return next;
    });
  };

  const handleCreateGeometry = async (args: {
    geometryKind: "point" | "line" | "polyline" | "rectangle" | "polygon";
    geometry: FieldPointGeometry | OverlayGeometry;
    measurementType?: MeasurementType;
    quantityValue?: number;
    height?: number;
  }) => {
    if (history.isBusy) return;
    if (!routeJobId || !activePlanDocumentId || !effectiveSelectedTagId || !effectiveSelectedStatusId) {
      toast.error("Select a tag and status before drawing");
      return;
    }
    const isMeasurementGeometry = args.geometry.type !== "point";
    if (isMeasurementGeometry && !measurementScaleValue) {
      toast.error("Set the measurement scale for this page before measuring");
      return;
    }
    try {
      const parsedHeight = parseFeetInches(heightValue);
      const heightFeet = parsedHeight ?? 0;
      const measurementType = args.measurementType ?? activeMeasurementType;
      const geometry = toFieldGeometry(args.geometryKind, args.geometry);
      const createArgs = {
        jobId: routeJobId,
        planDocumentId: activePlanDocumentId,
        pageNumber: activePage,
        tagId: effectiveSelectedTagId as Id<"fieldPilotTags">,
        statusId: effectiveSelectedStatusId as Id<"fieldPilotStatuses">,
        geometryKind: args.geometryKind,
        geometry,
        itemKind: isMeasurementGeometry ? ("measurement" as const) : ("manual" as const),
        measurementType: isMeasurementGeometry ? measurementType : undefined,
        quantityValue:
          args.quantityValue ??
          getGeometryQuantity(
            args.geometryKind,
            args.geometry,
            measurementType,
            heightFeet,
            measurementScaleValue?.pixelsPerFoot ?? 1,
            measurementScaleValue?.pageAspectRatio
          ),
        height:
          args.height ??
          (isMeasurementGeometry && measurementRequiresHeight(args.geometryKind as ToolType, measurementType)
            ? heightFeet
            : undefined),
      };
      const itemId = await createItem(createArgs);
      history.record({
        undo: async () => {
          await removeItems({ jobId: routeJobId, itemIds: [itemId] });
        },
        redo: async () => {
          await restoreItems({ jobId: routeJobId, itemIds: [itemId] });
        },
      });
      toast.success("Field item added");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to add FieldPilot item");
    }
  };

  const handleDraftMeasurementChange = (draft: DraftMeasurementState | null) => {
    setDraftMeasurement(draft);
    if (!draft) return;
    void handleCreateGeometry({
      geometryKind: draft.tool,
      geometry: draft.geometry,
      measurementType: draft.measurementType,
      quantityValue: draft.value,
      height: draft.height,
    }).finally(() => setDraftMeasurement(null));
  };

  const applyStatusToSelected = async (statusId: Id<"fieldPilotStatuses">) => {
    if (!routeJobId || selectedItems.length === 0 || history.isBusy) return;
    const snapshots = [...selectedItems];
    const itemIds = snapshots.map((item) => item._id);
    try {
      await updateStatusMany({
        jobId: routeJobId,
        itemIds,
        statusId,
      });
      history.record({
        undo: async () => {
          await restoreItemStates({
            jobId: routeJobId,
            states: snapshots.map((item) => ({
              itemId: item._id,
              tagId: item.tagId,
              statusId: item.statusId,
              subTaskId: item.subTaskId,
            })),
          });
        },
        redo: async () => {
          await updateStatusMany({
            jobId: routeJobId,
            itemIds: snapshots.map((item) => item._id),
            statusId,
          });
        },
      });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update status");
    }
  };

  const applyTagToSelected = async (tagId: Id<"fieldPilotTags">) => {
    if (!routeJobId || selectedItemIds.size === 0 || history.isBusy) return;
    const snapshots = [...selectedItems];
    try {
      await updateTagMany({
        jobId: routeJobId,
        itemIds: Array.from(selectedItemIds) as Id<"fieldPilotItems">[],
        tagId,
      });
      history.record({
        undo: async () => {
          await restoreItemStates({
            jobId: routeJobId,
            states: snapshots.map((item) => ({
              itemId: item._id,
              tagId: item.tagId,
              statusId: item.statusId,
              subTaskId: item.subTaskId,
            })),
          });
        },
        redo: async () => {
          await updateTagMany({
            jobId: routeJobId,
            itemIds: snapshots.map((item) => item._id),
            tagId,
          });
        },
      });
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to update tag");
    }
  };

  const deleteItemsWithHistory = async (snapshots: Doc<"fieldPilotItems">[]) => {
    if (!routeJobId || snapshots.length === 0 || history.isBusy) return;
    const itemIds = snapshots.map((item) => item._id);
    try {
      await removeItems({
        jobId: routeJobId,
        itemIds,
      });
      history.record({
        undo: async () => {
          await restoreItems({ jobId: routeJobId, itemIds });
        },
        redo: async () => {
          await removeItems({ jobId: routeJobId, itemIds });
        },
      });
      toast.success("Field items removed");
      setSelectedItemIds(new Set());
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to remove items");
    }
  };

  const deleteSelectedItems = async () => {
    await deleteItemsWithHistory(selectedItems);
  };

  const updateFieldMeasurementGeometry = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    geometry: MeasurementGeometry | PolygonGeometry,
    height: number | undefined,
    value: number
  ) => {
    if (history.isBusy) return;
    const currentItem = visibleFieldItems.find((item) => String(item._id) === String(measurementId));
    if (!currentItem) return;
    const nextGeometry = toFieldGeometry(currentItem.geometryKind as FieldGeometryKind, geometry);
    setPendingFieldEditCount((current) => current + 1);
    try {
      await updateItem({
        itemId: currentItem._id,
        geometryKind: currentItem.geometryKind as FieldGeometryKind,
        geometry: nextGeometry,
        height,
        clearHeight: height === undefined,
        quantityValue: value,
      });
      history.record({
        undo: async () => {
          await updateItem({
            itemId: currentItem._id,
            geometryKind: currentItem.geometryKind as FieldGeometryKind,
            geometry: currentItem.geometry,
            height: currentItem.height,
            clearHeight: currentItem.height === undefined,
            quantityValue: currentItem.quantityValue,
          });
        },
        redo: async () => {
          await updateItem({
            itemId: currentItem._id,
            geometryKind: currentItem.geometryKind as FieldGeometryKind,
            geometry: nextGeometry,
            height,
            clearHeight: height === undefined,
            quantityValue: value,
          });
        },
      });
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to update measurement"));
      throw error;
    } finally {
      setPendingFieldEditCount((current) => Math.max(0, current - 1));
    }
  };

  const updateFieldMeasurementAnnotation = async (
    measurementId: Id<"takeoffPilotMeasurements">,
    annotation: MeasurementAnnotation
  ) => {
    if (history.isBusy) return;
    const currentItem = visibleFieldItems.find((item) => String(item._id) === String(measurementId));
    if (!currentItem) return;
    return enqueueFieldItemAnnotationEdit(currentItem._id, async () => {
      const key = String(currentItem._id);
      const previousAnnotation = latestFieldItemAnnotationRef.current.has(key)
        ? latestFieldItemAnnotationRef.current.get(key)
        : currentItem.annotation;
      const nextAnnotation = { ...(previousAnnotation ?? {}), ...annotation };
      latestFieldItemAnnotationRef.current.set(key, nextAnnotation);
      try {
        await updateItem({ itemId: currentItem._id, annotation: nextAnnotation });
        history.record({
          undo: async () => {
            await updateItem({
              itemId: currentItem._id,
              annotation: previousAnnotation,
              clearAnnotation: previousAnnotation === undefined,
            });
            latestFieldItemAnnotationRef.current.set(key, previousAnnotation);
          },
          redo: async () => {
            await updateItem({ itemId: currentItem._id, annotation: nextAnnotation });
            latestFieldItemAnnotationRef.current.set(key, nextAnnotation);
          },
        });
      } catch (error) {
        latestFieldItemAnnotationRef.current.set(key, previousAnnotation);
        notifyError(error, getUserFacingErrorMessage(error, "Failed to update measurement label"));
        throw error;
      }
    });
  };

  const applyFieldMeasurementStyle = async (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    updates: { leaderVisible: boolean } | { strokeWidth: number }
  ) => {
    if (!routeJobId || history.isBusy || measurementIds.length === 0) {
      return false;
    }
    const snapshots = measurementIds.flatMap((measurementId) => {
      const item = visibleFieldItems.find((candidate) => String(candidate._id) === String(measurementId));
      if (!item) return [];
      const key = String(item._id);
      const annotation = latestFieldItemAnnotationRef.current.has(key)
        ? latestFieldItemAnnotationRef.current.get(key)
        : item.annotation;
      return [{ itemId: item._id, annotation }];
    });
    if (snapshots.length !== measurementIds.length) {
      return false;
    }
    const saveUpdates = async () => {
      if ("leaderVisible" in updates) {
        await applyLeaderMany({
          jobId: routeJobId,
          itemIds: snapshots.map((snapshot) => snapshot.itemId),
          leaderVisible: updates.leaderVisible,
        });
      } else {
        await applyStrokeWidthMany({
          jobId: routeJobId,
          itemIds: snapshots.map((snapshot) => snapshot.itemId),
          strokeWidth: updates.strokeWidth,
        });
      }
    };
    const snapshotKeys = snapshots.map((snapshot) => String(snapshot.itemId));
    for (const key of snapshotKeys) {
      beginFieldItemAnnotationEdit(key);
    }
    for (const snapshot of snapshots) {
      latestFieldItemAnnotationRef.current.set(String(snapshot.itemId), {
        ...(snapshot.annotation ?? {}),
        ...updates,
      });
    }
    setPendingFieldEditCount((current) => current + 1);
    try {
      await saveUpdates();
      history.record({
        undo: async () => {
          await restoreAnnotationsMany({
            jobId: routeJobId,
            states: snapshots.map((snapshot) => ({
              itemId: snapshot.itemId,
              annotation: snapshot.annotation,
              clearAnnotation: snapshot.annotation === undefined,
            })),
          });
          for (const snapshot of snapshots) {
            latestFieldItemAnnotationRef.current.set(String(snapshot.itemId), snapshot.annotation);
          }
        },
        redo: async () => {
          await saveUpdates();
          for (const snapshot of snapshots) {
            latestFieldItemAnnotationRef.current.set(String(snapshot.itemId), {
              ...(snapshot.annotation ?? {}),
              ...updates,
            });
          }
        },
      });
      return true;
    } catch (error) {
      for (const snapshot of snapshots) {
        latestFieldItemAnnotationRef.current.set(String(snapshot.itemId), snapshot.annotation);
      }
      if ("strokeWidth" in updates && getOrganizationAccessError(error)) throw error;
      if ("leaderVisible" in updates) {
        notifyError(error, getUserFacingErrorMessage(error, "Failed to update measurement leaders"));
      }
      return false;
    } finally {
      for (const key of snapshotKeys) {
        endFieldItemAnnotationEdit(key);
      }
      setPendingFieldEditCount((current) => Math.max(0, current - 1));
    }
  };

  const applyFieldMeasurementLeaders = (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    leaderVisible: boolean
  ) => applyFieldMeasurementStyle(measurementIds, { leaderVisible });

  const applyFieldStrokeWidth = (
    measurementIds: Id<"takeoffPilotMeasurements">[],
    strokeWidth: number
  ) => applyFieldMeasurementStyle(measurementIds, { strokeWidth });

  const applyFieldGroupLabels = async (
    measurementIds: Id<"takeoffPilotMeasurements">[]
  ) => {
    if (!routeJobId || history.isBusy || measurementIds.length < 2) {
      return false;
    }
    try {
      await groupItemLabels({
        jobId: routeJobId,
        itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
      });
      history.record({
        undo: async () => {
          await ungroupItemLabels({
            jobId: routeJobId,
            itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
          });
        },
        redo: async () => {
          await groupItemLabels({
            jobId: routeJobId,
            itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
          });
        },
      });
      return true;
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to group labels"));
      return false;
    }
  };

  const applyFieldUngroupLabels = async (
    measurementIds: Id<"takeoffPilotMeasurements">[]
  ) => {
    if (!routeJobId || history.isBusy || measurementIds.length === 0) {
      return false;
    }
    try {
      await ungroupItemLabels({
        jobId: routeJobId,
        itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
      });
      history.record({
        undo: async () => {
          await groupItemLabels({
            jobId: routeJobId,
            itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
          });
        },
        redo: async () => {
          await ungroupItemLabels({
            jobId: routeJobId,
            itemIds: measurementIds as unknown as Id<"fieldPilotItems">[],
          });
        },
      });
      return true;
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to ungroup labels"));
      return false;
    }
  };

  const applyFieldGroupAllLabelsInScope = async () => {
    if (!routeJobId || !activePlanDocumentId || history.isBusy) {
      return false;
    }
    const selected = selectedItems.filter((item) => item.geometry.type !== "point");
    if (selected.length === 0) {
      toast.error("Select measurement items to group labels");
      return false;
    }
    const tagIds = new Set(selected.map((item) => String(item.tagId)));
    const subTaskKeys = new Set(selected.map((item) => (item.subTaskId ? String(item.subTaskId) : "")));
    if (tagIds.size !== 1 || subTaskKeys.size !== 1) {
      toast.error("Select items from a single tag and sub-task to group all labels");
      return false;
    }
    const scopeArgs = {
      jobId: routeJobId,
      planDocumentId: activePlanDocumentId,
      pageNumber: activePage,
      tagId: selected[0]!.tagId,
      subTaskId: selected[0]!.subTaskId,
    };
    try {
      await groupAllItemLabelsForScope(scopeArgs);
      history.record({
        undo: async () => {
          await ungroupAllItemLabelsForScope(scopeArgs);
        },
        redo: async () => {
          await groupAllItemLabelsForScope(scopeArgs);
        },
      });
      return true;
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to group all labels"));
      return false;
    }
  };

  const applyFieldUngroupAllLabelsInScope = async () => {
    if (!routeJobId || !activePlanDocumentId || history.isBusy) {
      return false;
    }
    const selected = selectedItems.filter((item) => item.geometry.type !== "point");
    if (selected.length === 0) {
      toast.error("Select measurement items to ungroup labels");
      return false;
    }
    const tagIds = new Set(selected.map((item) => String(item.tagId)));
    const subTaskKeys = new Set(selected.map((item) => (item.subTaskId ? String(item.subTaskId) : "")));
    if (tagIds.size !== 1 || subTaskKeys.size !== 1) {
      toast.error("Select items from a single tag and sub-task to ungroup all labels");
      return false;
    }
    const scopeArgs = {
      jobId: routeJobId,
      planDocumentId: activePlanDocumentId,
      pageNumber: activePage,
      tagId: selected[0]!.tagId,
      subTaskId: selected[0]!.subTaskId,
    };
    try {
      await ungroupAllItemLabelsForScope(scopeArgs);
      history.record({
        undo: async () => {
          await groupAllItemLabelsForScope(scopeArgs);
        },
        redo: async () => {
          await ungroupAllItemLabelsForScope(scopeArgs);
        },
      });
      return true;
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to ungroup all labels"));
      return false;
    }
  };

  const updateFieldPoint = (item: Doc<"fieldPilotItems">, point: DraftPoint) => {
    if (history.isBusy) return;
    const key = String(item._id);
    const nextGeometry = { type: "point" as const, ...point };
    setPendingFieldEditCount((current) => current + 1);
    pointUpdateQueueRef.current = pointUpdateQueueRef.current
      .catch(() => undefined)
      .then(async () => {
        const previousGeometry = latestPointGeometryRef.current.get(key) ?? item.geometry;
        await updateItem({
          itemId: item._id,
          geometryKind: "point",
          geometry: nextGeometry,
          quantityValue: 1,
        });
        latestPointGeometryRef.current.set(key, nextGeometry);
        history.record({
          undo: async () => {
            await updateItem({ itemId: item._id, geometryKind: "point", geometry: previousGeometry, quantityValue: 1 });
            latestPointGeometryRef.current.set(key, previousGeometry);
          },
          redo: async () => {
            await updateItem({ itemId: item._id, geometryKind: "point", geometry: nextGeometry, quantityValue: 1 });
            latestPointGeometryRef.current.set(key, nextGeometry);
          },
        });
      })
      .catch((error) => {
        notifyError(error, error instanceof Error ? error.message : "Failed to update point");
      })
      .finally(() => {
        setPendingFieldEditCount((current) => Math.max(0, current - 1));
      });
  };

  const copySelectedItems = () => {
    if (selectedItems.length === 0) return;
    setCopiedItems(selectedItems);
    toast.success(`${selectedItems.length} field item${selectedItems.length === 1 ? "" : "s"} copied`);
  };

  const duplicateItems = async (items: Doc<"fieldPilotItems">[]) => {
    if (!routeJobId || !activePlanDocumentId || items.length === 0 || history.isBusy) return;
    const duplicatedIds: string[] = [];
    const duplicatedItemIds: Id<"fieldPilotItems">[] = [];
    try {
      for (const item of items) {
        const geometry = translateFieldGeometry(item.geometry);
        const createArgs = {
          ...getFieldItemCreateArgs(item),
          jobId: routeJobId,
          planDocumentId: activePlanDocumentId,
          pageNumber: activePage,
          geometryKind: item.geometryKind as FieldGeometryKind,
          geometry,
        };
        const itemId = await createItem(createArgs);
        duplicatedIds.push(String(itemId));
        duplicatedItemIds.push(itemId);
      }
      history.record({
        undo: async () => {
          await removeItems({ jobId: routeJobId, itemIds: duplicatedItemIds });
        },
        redo: async () => {
          await restoreItems({ jobId: routeJobId, itemIds: duplicatedItemIds });
        },
      });
      setSelectedItemIds(new Set(duplicatedIds));
      toast.success(`${duplicatedIds.length} field item${duplicatedIds.length === 1 ? "" : "s"} duplicated`);
    } catch (error) {
      if (duplicatedItemIds.length > 0) {
        try {
          await removeItems({ jobId: routeJobId, itemIds: duplicatedItemIds });
        } catch (rollbackError) {
          console.error("Failed to roll back partial duplication", rollbackError);
        }
      }
      notifyError(error, error instanceof Error ? error.message : "Failed to duplicate field items");
    }
  };

  const pasteCopiedItems = async () => {
    await duplicateItems(copiedItems);
  };

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.defaultPrevented) return;
      const target = event.target as HTMLElement | null;
      const isEditableTarget = Boolean(
        target &&
          (target.tagName === "INPUT" || target.tagName === "TEXTAREA" || target.isContentEditable)
      );
      if (isEditableTarget || !canEdit) return;

      const isModifier = event.metaKey || event.ctrlKey;
      if (isModifier && event.key.toLowerCase() === "c" && selectedItemIds.size > 0) {
        copySelectedItems();
        event.preventDefault();
        return;
      }
      if (isModifier && event.key.toLowerCase() === "v" && copiedItems.length > 0) {
        void pasteCopiedItems();
        event.preventDefault();
        return;
      }
      if (isModifier && event.key.toLowerCase() === "d" && selectedItems.length > 0) {
        void duplicateItems(selectedItems);
        event.preventDefault();
        return;
      }
      if ((event.key === "Delete" || event.key === "Backspace") && selectedItemIds.size > 0) {
        void deleteSelectedItems();
        event.preventDefault();
      }
    };
    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [canEdit, copiedItems, selectedItemIds, selectedItems, routeJobId, activePlanDocumentId, activePage]);

  const handleCreateStatus = async () => {
    if (!routeJobId) return;
    const trimmed = newStatusName.trim();
    if (!trimmed) return;
    setIsCreatingStatus(true);
    try {
      await createStatus({ jobId: routeJobId, name: trimmed, colorHex: newStatusColor });
      toast.success("Status added");
      setNewStatusName("");
      setNewStatusColor("#22C55E");
    } catch (error) {
      notifyError(error, error instanceof Error ? error.message : "Failed to add status");
    } finally {
      setIsCreatingStatus(false);
    }
  };

  const handleUpdateStatusDefinition = async (args: {
    statusId: Id<"fieldPilotStatuses">;
    name?: string;
    colorHex?: string;
  }) => {
    await updateStatusDefinition(args);
  };

  const handleDeleteStatusDefinition = async (statusId: Id<"fieldPilotStatuses">) => {
    await deleteStatusDefinition({ statusId });
  };

  const seedExportOptionsFromDisplay = () => {
    const seeded = fieldPilotExportOptionsFromDisplay({
      colorizeBy,
      showMeasurementValues,
      viewportAnnotation,
    });
    setExportColorizeBy(seeded.colorizeBy);
    setExportShowMeasurementValues(seeded.showMeasurementValues);
    setExportLabelAnnotation(seeded.labelAnnotation);
  };

  const handleExportDialogOpenChange = (open: boolean) => {
    if (open) {
      seedExportOptionsFromDisplay();
    }
    setIsExportDialogOpen(open);
  };

  const handleExportPdf = async (fileName: string) => {
    if (!routeJobId) return;
    setIsExporting(true);
    try {
      const result = await generatePdfExport({
        jobId: routeJobId,
        timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone,
        saveAsDailyLog,
        includeSchedule,
        scheduleColumns: DEFAULT_FIELD_PILOT_SCHEDULE_COLUMNS,
        includeMeasurementBreakdown,
        // Plan markup is the core of the export and always included.
        includePlanMarkup: true,
        includeProgressBars,
        includeGanttChart,
        statusFilterId:
          exportStatusFilterId && exportStatusFilterId !== "all"
            ? (exportStatusFilterId as Id<"fieldPilotStatuses">)
            : undefined,
        colorizeBy: exportColorizeBy,
        showMeasurementValues: exportShowMeasurementValues,
        labelAnnotation: exportLabelAnnotation,
      });
      autoDownloadNameRef.current = fileName;
      setAutoDownloadPdfArtifactId(String(result.artifactId));
      await exportFilenames.saveForArtifact(result.artifactId, fileName);
      setIsExportDialogOpen(false);
      toast.success("FieldPilot PDF export started. Download will start when ready.");
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to start FieldPilot export"));
    } finally {
      setIsExporting(false);
    }
  };

  const handleCreateCustomLineItem = async ({ rawValue }: { rawValue: string }) => {
    if (!routeJobId) return;
    try {
      await createCustomTag({ jobId: routeJobId, rawValue });
      toast.success("Custom line item added");
    } catch (error) {
      notifyError(error, getUserFacingErrorMessage(error, "Failed to add custom line item"));
      throw error;
    }
  };

  const toggleExpandTag = (tagId: string) => {
    setExpandedTagIds((current) => {
      const next = new Set(current);
      if (next.has(tagId)) {
        next.delete(tagId);
      } else {
        next.add(tagId);
      }
      return next;
    });
  };

  const handleReorderStatus = (
    statusId: Id<"fieldPilotStatuses">,
    direction: "up" | "down"
  ) => {
    if (!routeJobId) return;
    const ids = (details?.statuses ?? []).map((status) => status._id);
    const index = ids.findIndex((id) => String(id) === String(statusId));
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= ids.length) return;
    const next = [...ids];
    [next[index], next[target]] = [next[target], next[index]];
    reorderStatuses({ jobId: routeJobId, orderedStatusIds: next }).catch((error) =>
      notifyError(error, getUserFacingErrorMessage(error, "Failed to reorder statuses"))
    );
  };

  const openGroupSubTaskDialog = () => {
    const distinctTagIds = Array.from(new Set(selectedItems.map((item) => String(item.tagId))));
    const tagId = distinctTagIds.length === 1 ? (selectedItems[0]?.tagId ?? null) : null;
    const tagName = tagId
      ? scheduleByTagId.get(String(tagId))?.displayName ??
        (details?.tags ?? []).find((tag) => String(tag._id) === String(tagId))?.rawValue
      : undefined;
    setSubTaskDialogTarget({
      mode: "create",
      tagId,
      tagName,
      itemIds: selectedItems.map((item) => item._id),
      distinctTagCount: distinctTagIds.length,
    });
    setIsSubTaskDialogOpen(true);
  };

  /** Collect a task's ungrouped remainder into one new sub-task, named in the usual dialog. */
  const openGroupUngroupedDialog = (
    tagId: Id<"fieldPilotTags">,
    tagName: string,
    ungroupedItemCount: number
  ) => {
    setSubTaskDialogTarget({
      mode: "create",
      tagId,
      tagName,
      groupUngrouped: true,
      ungroupedItemCount,
      distinctTagCount: 1,
    });
    setIsSubTaskDialogOpen(true);
  };

  const openEditSubTaskDialog = (
    subTaskId: Id<"fieldPilotSubTasks">,
    subTaskName?: string
  ) => {
    setSubTaskDialogTarget({
      mode: "edit",
      subTaskId,
      subTaskName: subTaskName ?? subTaskNameById.get(String(subTaskId)) ?? "",
    });
    setIsSubTaskDialogOpen(true);
  };

  const handleDeleteEmptySubTask = (subTaskId: Id<"fieldPilotSubTasks">) => {
    removeEmptySubTask({ subTaskId })
      .then(() => toast.success("Sub-task deleted"))
      .catch((error) => notifyError(error, getUserFacingErrorMessage(error, "Failed to delete sub-task")));
  };

  // "Milestones" for a single item: ensure it has a sub-task (auto-create "Schedule" when
  // ungrouped), then open the milestone editor (CON-370). A newly created schedule is discarded
  // if the dialog is cancelled.
  const openMilestonesForItem = (item: Doc<"fieldPilotItems">) => {
    void (async () => {
      try {
        const result = await ensureSoloFromItem({ itemId: item._id });
        setSubTaskDialogTarget({
          mode: "edit",
          subTaskId: result.subTaskId,
          subTaskName: result.name,
          createdForMilestones: result.created,
          createdForMilestonesItemId: result.created ? item._id : undefined,
        });
        setIsSubTaskDialogOpen(true);
      } catch (error) {
        notifyError(error, getUserFacingErrorMessage(error, "Failed to open item schedule"));
      }
    })();
  };

  const handleToggleScheduleColumns = useCallback(
    (next: boolean) => {
      setShowScheduleColumns(next);
      if (!routeJobId) return;
      try {
        localStorage.setItem(
          `${FIELD_PILOT_SCHEDULE_COLUMNS_KEY_PREFIX}:${routeJobId}`,
          next ? "true" : "false"
        );
      } catch {
        // Keep the toggle working even when storage is unavailable.
      }
    },
    [routeJobId]
  );

  const handleDownloadPdf = useCallback(async (fileName?: string) => {
    if (!pdfExportDownloadUrl) return false;
    try {
      const response = await fetch(pdfExportDownloadUrl);
      if (!response.ok) {
        throw new Error("Failed to fetch file");
      }
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const anchor = document.createElement("a");
      anchor.href = url;
      anchor.download = withExportExtension(fileName ?? fieldReportName, "pdf");
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
      toast.success("Download started");
      return true;
    } catch (error) {
      notifyError(error, "Failed to download PDF export");
      console.error(error);
      return false;
    }
  }, [fieldReportName, pdfExportDownloadUrl]);

  useEffect(() => {
    if (!autoDownloadPdfArtifactId) return;
    const currentArtifactId = pdfExport?.artifactId ? String(pdfExport.artifactId) : null;
    if (currentArtifactId !== autoDownloadPdfArtifactId) return;

    if (pdfExport?.status === GLOBAL_PROCESSING_FAILED) {
      toast.error(pdfExport.error ?? "FieldPilot PDF export failed");
      setAutoDownloadPdfArtifactId(null);
      return;
    }

    if (pdfExport?.status !== GLOBAL_PROCESSING_COMPLETED || !pdfExportDownloadUrl) return;
    if (autoDownloadPdfInFlightRef.current === autoDownloadPdfArtifactId) return;

    autoDownloadPdfInFlightRef.current = autoDownloadPdfArtifactId;
    void (async () => {
      try {
        await handleDownloadPdf(autoDownloadNameRef.current ?? undefined);
      } catch (error) {
        console.error("Failed to auto-download FieldPilot export:", error);
      } finally {
        autoDownloadPdfInFlightRef.current = null;
        setAutoDownloadPdfArtifactId(null);
      }
    })();
  }, [
    autoDownloadPdfArtifactId,
    handleDownloadPdf,
    pdfExport?.artifactId,
    pdfExport?.error,
    pdfExport?.status,
    pdfExportDownloadUrl,
  ]);

  const fieldPdfExportStatus: ProcessingStateWithIdle =
    pdfExport?.status === GLOBAL_PROCESSING_PENDING ||
    pdfExport?.status === GLOBAL_PROCESSING_PROCESSING ||
    pdfExport?.status === GLOBAL_PROCESSING_COMPLETED ||
    pdfExport?.status === GLOBAL_PROCESSING_FAILED
      ? pdfExport.status
      : "idle";
  const isPdfExportInProgress = fieldPdfExportStatus === GLOBAL_PROCESSING_PENDING || fieldPdfExportStatus === GLOBAL_PROCESSING_PROCESSING || isExporting;

  if (!routeJobId || !details) {
    // Same chrome as TakeoffPilot's loading state: the exit stays reachable and the page doesn't jump.
    return (
      <div className="relative flex h-full flex-col overflow-hidden bg-background">
        <ViewerTopBar
          back={<ViewerBackButton label="Back to FieldPilot" onBack={handleExitViewer} />}
          title="FieldPilot"
          readOnly={!canEdit}
        />
        <SpinnerState className="min-h-0 flex-1" />
      </div>
    );
  }

  return (
    <div className="relative h-full overflow-hidden bg-background">
      <div className="pointer-events-none absolute inset-x-0 top-0 z-[60] flex">
        <ViewerTopBar
          className="pointer-events-auto"
          back={<ViewerBackButton label="Back to FieldPilot" onBack={handleExitViewer} />}
          title={details.job.name}
          subtitle={details.job.scheduleRevisionName ?? "No schedule"}
          readOnly={!canEdit}
          source={
            <>
              <ViewerSourceSelect
                sources={planSources}
                value={activeViewerSource?.sourceId ?? ""}
                onValueChange={(value) => {
                  setActiveSourceId(value);
                  setActivePage(1);
                }}
                label="Drawing"
                placeholder="Select drawing"
              />
              <ViewerPager
                activePage={activePage}
                pageCount={pageCount}
                onGoToPage={(page) => viewportRef.current?.goToPage(page)}
                pageLabels={pageLabels}
              />
              <PacketSourceSheetButton
                revisionId={activeViewerSource?.revisionId}
                pageNumber={activePage}
              />
            </>
          }
          meta={
            // Draw-time defaults: the tag + status stamped onto each new item. Only relevant while a
            // draw/point tool is engaged (consumed in handleCreateGeometry), so reveal them then.
            activeTool ? (
              <div className="flex shrink-0 items-center gap-2">
                <span className="text-xs font-medium text-muted-foreground">Draw as</span>
                <Select value={effectiveSelectedTagId} onValueChange={setSelectedTagId}>
                  <SelectTrigger className="h-8 w-32 text-sm" aria-label="Draw as tag">
                    <SelectValue placeholder="Tag" />
                  </SelectTrigger>
                  <SelectContent className="z-[160]">
                    {activeTags.map((tag) => (
                      <SelectItem key={tag._id} value={tag._id}>
                        {resolveTagLabel(tag)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={effectiveSelectedStatusId} onValueChange={setSelectedStatusId}>
                  <SelectTrigger className="h-8 w-40 text-sm" aria-label="Initial status">
                    <SelectValue placeholder="Initial status" />
                  </SelectTrigger>
                  <SelectContent className="z-[160]">
                    {details.statuses.map((status) => (
                      <SelectItem key={status._id} value={status._id}>
                        <StatusOptionLabel colorHex={status.colorHex} name={status.name} />
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : null
          }
          tools={
            <>
              {/* Display options: color basis + measurement values + status label */}
              <FieldPilotLayersMenu
                visibility={layerVisibility}
                onVisibilityChange={setLayerVisible}
                counts={layerCounts}
              />
              <Popover>
                <PopoverTrigger asChild>
                  <Button type="button" variant="outline" size="sm" aria-label="Display options">
                    <SlidersHorizontal />
                    <span className="hidden sm:inline">Display</span>
                  </Button>
                </PopoverTrigger>
                <PopoverContent align="end" className="z-[160] w-64 space-y-4">
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-muted-foreground">Color</span>
                    <SegmentedControl
                      aria-label="Color items by"
                      size="compact"
                      value={colorizeBy}
                      onValueChange={setColorizeBy}
                      options={COLORIZE_BY_OPTIONS}
                    />
                  </div>
                  <label className="flex cursor-pointer items-center justify-between gap-3 text-xs font-medium text-muted-foreground">
                    Measurements
                    <Switch
                      checked={showMeasurementValues}
                      onCheckedChange={setShowMeasurementValues}
                      aria-label="Show measurement values"
                    />
                  </label>
                  <div className="flex items-center justify-between gap-3">
                    <span className="text-xs font-medium text-muted-foreground">Status</span>
                    <Select
                      value={viewportAnnotation}
                      onValueChange={(value) => setViewportAnnotation(value as FieldPilotAnnotationMode)}
                    >
                      <SelectTrigger className="h-8 w-28 text-xs" aria-label="Status label">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="z-[160]">
                        <SelectItem value="current">Current</SelectItem>
                        <SelectItem value="off">Off</SelectItem>
                        <SelectItem value="next">Next</SelectItem>
                        <SelectItem value="all">All</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </PopoverContent>
              </Popover>

              {canEdit ? (
                <>
                  <div aria-hidden="true" className="mx-1 h-6 w-px shrink-0 bg-border" />
                  <UndoRedoControls
                    canUndo={history.canUndo}
                    canRedo={history.canRedo}
                    isBusy={isFieldHistoryControlBusy(history.isBusy, pendingFieldEditCount)}
                    onUndo={() => void handleUndo()}
                    onRedo={() => void handleRedo()}
                  />
                  <ViewportToolsetsHeader enabledToolsets={headerToolsets} controllers={railControllers} />
                </>
              ) : null}

              <Button
                type="button"
                size="sm"
                variant="outline"
                aria-label="Daily logs"
                onClick={() => setIsDailyLogsDialogOpen(true)}
              >
                <FileText />
                <span className="hidden sm:inline">Daily Logs</span>
              </Button>
            </>
          }
          actions={
            <ExportButton
              onClick={() => handleExportDialogOpenChange(true)}
              busy={isPdfExportInProgress}
            />
          }
        />
      </div>

      {documentSource ? (
          <DocumentViewportHost
            source={documentSource}
            activePage={activePage}
            onActivePageChange={setActivePage}
            onPageCountChange={setPageCount}
            pageLabels={pageLabels}
            pointerOwner={
              measurementScale.isCalibrationMode ||
              activeTool ||
              activeTagTool ||
              activeToolsetId === VIEWPORT_TOOLSET_IDS.tags
                ? "tool"
                : "viewport"
            }
            enableWheelZoom={!measurementScale.isCalibrationMode}
            viewportPadding={{ top: 80, right: 24, bottom: 28, left: 88 }}
            panBoundaryPadding={{ bottom: (isPanelOpen ? panelHeight : 0) + 120 }}
            className="absolute inset-0 overflow-hidden pt-12"
            leftRail={
              canEdit ? <ViewportToolsetsRail enabledToolsets={railToolsets} controllers={railControllers} /> : null
            }
          >
            <FieldViewportBinder onViewportChange={handleViewportChange} />
            <FieldSelectedItemCard
              item={activeSelectedItem}
              selectedCount={selectedItems.length}
              selectedMeasurements={selectedMeasurements}
              itemLabel={
                activeSelectedItem
                  ? resolveItemLabel({
                      subTaskId: activeSelectedItem.subTaskId,
                      subTaskNameById,
                      tagName:
                        scheduleByTagId.get(String(activeSelectedItem.tagId))?.displayName ??
                        activeTags.find((tag) => String(tag._id) === String(activeSelectedItem.tagId))
                          ?.rawValue ??
                        activeSelectedItem.geometryKind,
                    })
                  : ""
              }
              tags={activeTags}
              statuses={details.statuses}
              subTasks={selectionSubTasks}
              subTaskSelectValue={subTaskSelectValue}
              subTaskPlaceholder={subTaskPlaceholder}
              tagSelectValue={tagSelectValue}
              tagPlaceholder={tagPlaceholder}
              statusSelectValue={statusSelectValue}
              statusPlaceholder={statusPlaceholder}
              spansMultipleTags={selectionSpansMultipleTags}
              onApplyTag={applyTagToSelected}
              onApplyStatus={applyStatusToSelected}
              onAssignToSubTask={assignSelectionToSubTask}
              onClearSubTask={clearSelectionSubTask}
              onApplyMeasurementLeaders={applyFieldMeasurementLeaders}
              onApplyStrokeWidth={applyFieldStrokeWidth}
              onPreviewStrokeWidth={previewFieldStrokeWidth}
              onGroupLabels={applyFieldGroupLabels}
              onUngroupLabels={applyFieldUngroupLabels}
              onGroupAllLabelsInScope={applyFieldGroupAllLabelsInScope}
              onUngroupAllLabelsInScope={applyFieldUngroupAllLabelsInScope}
              onDuplicate={() => void duplicateItems(selectedItems)}
              onGroupIntoSubTask={openGroupSubTaskDialog}
              onOpenMilestones={() => {
                if (selectedSubTaskId) {
                  openEditSubTaskDialog(
                    selectedSubTaskId as Id<"fieldPilotSubTasks">,
                    subTaskNameById.get(selectedSubTaskId)
                  );
                } else if (activeSelectedItem) {
                  openMilestonesForItem(activeSelectedItem);
                }
              }}
              onDelete={() => void deleteSelectedItems()}
              onClose={() => setSelectedItemIds(new Set())}
            />
            <CalibrationOverlay
              calibrationState={measurementScale.calibrationState}
              isCalibrationMode={measurementScale.isCalibrationMode}
              onViewportClick={measurementScale.handleViewportClick}
              onUpdatePoint1={measurementScale.updatePoint1}
              onUpdatePoint2={measurementScale.updatePoint2}
            />
            {layerVisibility.measurements ? (
              <>
            <FieldMeasurementOverlay
              ref={fieldMeasurementOverlayRef}
              scale={measurementScaleValue}
              activeTool={isMeasurementTool(activeTool) ? activeTool : null}
              activeMeasurementType={activeMeasurementType}
              currentHeightFeet={parseFeetInches(heightValue) ?? undefined}
              snapEnabled={snapEnabled}
              angleLockEnabled={angleLockEnabled}
              incrementSnapEnabled={incrementSnapEnabled}
              incrementStepFeet={incrementStepFeet}
              textLocked={textLocked}
              textFontSize={textFontSize}
              showLabels={labelsVisible}
              showLeaders={labelsVisible}
              showValues={showMeasurementValues}
              measurements={measurementRecords}
              colorByTagId={measurementColorById}
              labelByTagId={measurementLabelById}
              annotationByTagId={measurementAnnotationById}
              draftMeasurement={draftMeasurement}
              draftTagId={effectiveSelectedStatusId as unknown as Id<"takeoffPilotExtractedTags">}
              tags={statusOptions}
              onDraftMeasurementChange={handleDraftMeasurementChange}
              onDraftTagChange={() => undefined}
              onSaveDraftMeasurement={async () => undefined}
              onCancelDraftMeasurement={() => setDraftMeasurement(null)}
              selectedMeasurementIds={selectedMeasurementIds}
              activeMeasurementId={activeMeasurementId}
              onSelectMeasurement={handleSelectMeasurement}
              onSelectMeasurements={handleSelectMeasurements}
              onUpdateMeasurement={updateFieldMeasurementGeometry}
              onUpdateMeasurementAnnotation={updateFieldMeasurementAnnotation}
              onApplyMeasurementLeaders={applyFieldMeasurementLeaders}
              onGroupLabels={applyFieldGroupLabels}
              onUngroupLabels={applyFieldUngroupLabels}
              onGroupAllLabelsInScope={applyFieldGroupAllLabelsInScope}
              onUngroupAllLabelsInScope={applyFieldUngroupAllLabelsInScope}
              onReassignMeasurement={async () => false}
              onDeleteMeasurement={async (measurementId) => {
                const item = visibleFieldItems.find(
                  (candidate) => String(candidate._id) === String(measurementId)
                );
                if (item) await deleteItemsWithHistory([item]);
                setSelectedItemIds((current) => {
                  const next = new Set(current);
                  next.delete(String(measurementId));
                  return next;
                });
              }}
              onDeleteMeasurements={async (measurementIds) => {
                const idSet = new Set(measurementIds.map(String));
                await deleteItemsWithHistory(
                  visibleFieldItems.filter((item) => idSet.has(String(item._id)))
                );
                setSelectedItemIds((current) => {
                  const next = new Set(current);
                  for (const measurementId of measurementIds) {
                    next.delete(String(measurementId));
                  }
                  return next;
                });
              }}
              onDuplicateMeasurement={(measurementId) => {
                const item = visibleFieldItems.find((fieldItem) => String(fieldItem._id) === String(measurementId));
                return item ? duplicateItems([item]) : undefined;
              }}
              onDuplicateMeasurements={(measurementIds) => {
                const idSet = new Set(measurementIds.map(String));
                return duplicateItems(visibleFieldItems.filter((item) => idSet.has(String(item._id))));
              }}
              onOpenMilestones={(measurementId) => {
                const item = visibleFieldItems.find(
                  (fieldItem) => String(fieldItem._id) === String(measurementId)
                );
                if (item) openMilestonesForItem(item);
              }}
              canPasteMeasurement={false}
              showDraftMeasurementCard={false}
              showSelectedMeasurementCard={false}
            />
            <FieldPointOverlay
              items={visibleFieldItems}
              statusById={statusById}
              colorByItemId={measurementColorById}
              labelByItemId={measurementLabelById}
              annotationByItemId={measurementAnnotationById}
              selectedItemIds={selectedItemIds}
              showLabels={labelsVisible}
              showValues={showMeasurementValues}
              textLocked={textLocked}
              textFontSize={textFontSize}
              onSelectItem={handleItemPointerDown}
              onUpdatePoint={updateFieldPoint}
            />
            <FieldPointDrawingLayer
              activeTool={activeTool}
              onCreatePoint={(point) => handleCreateGeometry({ geometryKind: "point", geometry: { type: "point", ...point } })}
            />
              </>
            ) : null}
            {layerVisibility.tags && activeViewerSource?.revisionId ? (
              <DocumentTagEditorOverlay
                revisionId={activeViewerSource.revisionId as Id<"projectDocumentRevisions">}
                activeDetections={pageTagDetections}
                archivedDetections={archivedPageTagDetections}
                getStyle={(detection) =>
                  getFieldPilotDetectionStyle(
                    tagColorById.get(String(detection.fieldPilotTagId)) ??
                      FIELD_PILOT_TAG_FALLBACK_COLOR
                  )
                }
                highlightedDetectionId={highlightedDetectionId}
                editMode={activeToolsetId === VIEWPORT_TOOLSET_IDS.tags}
                onEditModeChange={(value) => {
                  if (value) {
                    setActiveTagTool(TAG_TOOL_IDS.add);
                    setActiveToolsetId(VIEWPORT_TOOLSET_IDS.tags);
                    return;
                  }
                  setActiveTagTool(null);
                  setActiveToolsetId((current) =>
                    current === VIEWPORT_TOOLSET_IDS.tags ? null : current
                  );
                }}
                showEditToggle={false}
                hidePanelWhenNotEditing
                showPanelOnlyWhenSelection
                allowCreate={false}
                tagOptions={tagPlacementOptions}
                selectedAddTagOptionId={selectedAddTagOptionId}
                placementMode={getTagPlacementMode(activeTagTool)}
                getSelectedDetectionAction={(detection) => ({
                  label: detection.alreadyConverted
                    ? "Created"
                    : convertingDetectionId === String(detection._id)
                      ? "Converting…"
                      : "Convert",
                  disabled:
                    detection.alreadyConverted ||
                    detection.archivedAt !== undefined ||
                    convertingDetectionId !== null ||
                    history.isBusy,
                  onSelect: () => {
                    void handleConvertSingleDetection(detection);
                  },
                })}
                selectedDetectionIds={selectedTagDetectionIds}
                onSelectedDetectionIdsChange={(detectionIds) => {
                  setSelectedTagDetectionIds(detectionIds);
                  if (detectionIds.length > 0) {
                    setSelectedItemIds(new Set());
                  }
                }}
              />
            ) : null}
          </DocumentViewportHost>
        ) : planSources.length > 0 ? (
          <PreparingDocumentsState sources={planSources} onOpen={setActiveSourceId} canRetry={canEdit} />
        ) : (
          <div className="flex h-full items-center justify-center pt-12 text-sm text-muted-foreground">
            No plan drawing is available for this FieldPilot job.
          </div>
        )}

        {canEdit && documentSource && activePlanDocumentId && !measurementScaleValue && !measurementScale.isCalibrationMode ? (
          <div className="pointer-events-none absolute inset-x-0 top-20 z-20 flex justify-center px-4">
            <div className="pointer-events-auto flex max-w-xl items-center gap-3 rounded-xl border bg-background/95 px-4 py-3 text-sm shadow-lg backdrop-blur">
              <Ruler className="size-4 shrink-0 text-muted-foreground" />
              <span className="text-muted-foreground">
                Set the page scale before taking measurements. Points and counts work without a scale.
              </span>
              <Button
                type="button"
                size="sm"
                onClick={() => scaleController.selectScaleCalibrationMethod(CALIBRATION_METHOD_IDS.manual)}
              >
                Set scale
              </Button>
            </div>
          </div>
        ) : null}

        {isPanelOpen ? (
        <ResizableBottomPanel
          isOpen={isPanelOpen}
          height={panelHeight}
          minHeight={220}
          maxHeight={getBottomPanelMaxHeight(innerHeight)}
          onHeightChange={setPanelHeight}
          header={
            <ResultsPanelHeader
              title="Status Progress"
              description="Per-tag status percentages use each tag's configured mode."
              onHide={() => setIsPanelOpen(false)}
              tools={
                <>
                  {scheduleSummary.scheduled > 0 ? (
                    <div className="flex items-center gap-2 text-xs font-medium">
                      {scheduleSummary.behind > 0 ? (
                        <span className="inline-flex items-center gap-1 text-status-error">
                          <StatusDot status="error" />
                          {scheduleSummary.behind} behind
                        </span>
                      ) : null}
                      <span className="inline-flex items-center gap-1 text-muted-foreground">
                        <StatusDot status="completed" />
                        {scheduleSummary.onTrack} on track
                      </span>
                    </div>
                  ) : null}
                  {canEdit ? (
                  <HoverCard openDelay={200} closeDelay={100}>
                    <HoverCardTrigger asChild>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setIsManageStatusesOpen(true)}
                      >
                        <ListOrdered />
                        Manage statuses
                      </Button>
                    </HoverCardTrigger>
                    {(details.statuses ?? []).length > 0 ? (
                      <HoverCardContent align="start" className="flex flex-col gap-1.5">
                        {(details.statuses ?? []).map((status) => (
                          <span key={status._id} className="flex items-center gap-2 text-xs font-medium">
                            <StatusDot colorHex={status.colorHex} />
                            {status.name}
                          </span>
                        ))}
                      </HoverCardContent>
                    ) : null}
                  </HoverCard>
                  ) : null}
                  <div className="flex items-center gap-2">
                    <Label htmlFor="status-filter" className="text-xs text-muted-foreground">Filter</Label>
                    <Select value={statusFilterId} onValueChange={setStatusFilterId}>
                      <SelectTrigger id="status-filter" aria-label="Filter by status" className="h-8 w-44">
                        <SelectValue placeholder="Filter status" />
                      </SelectTrigger>
                      <SelectContent className="z-[160]">
                        <SelectItem value="all">All statuses</SelectItem>
                        {details.statuses.map((status) => (
                          <SelectItem key={status._id} value={status._id}>
                            <StatusOptionLabel colorHex={status.colorHex} name={status.name} />
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <label className="flex cursor-pointer items-center gap-2 text-xs font-medium text-muted-foreground">
                    <Switch
                      checked={showScheduleColumns}
                      onCheckedChange={handleToggleScheduleColumns}
                      aria-label="Show schedule columns"
                    />
                    Schedule
                  </label>
                  {canEdit ? (
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setIsScheduleDialogOpen(true)}
                    >
                      <CalendarClock />
                      Edit schedule
                    </Button>
                  ) : null}
                </>
              }
            >
              {/* Choosing tags persists to the job; reviewers see the chosen tags as rows. */}
              {canEdit ? (
              <MultiSelect
                sections={tagSections}
                selected={selectedTagIds}
                onSelect={handleDropdownSelect}
                onRemove={handleDropdownRemove}
                onOpenChange={(open) => void handleDropdownOpenChange(open)}
                onSelectAll={handleSelectAllTags}
                onDeselectAll={handleDeselectAllTags}
                placeholder="Select tags..."
                triggerLabel="Tags"
                triggerIcon={<SlidersHorizontal className="size-3.5" />}
                triggerWidth="w-[20ch]"
                dropdownWidth="w-[520px]"
                columns="auto-fit"
                showSelectedBadges={false}
                wrapOptionLabels
                dropdownFooter={
                  <div className="flex flex-col gap-2">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setIsSearchTagsOpen(true)}
                    >
                      <Search className="size-3.5" />
                      Search tags
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      className="w-full"
                      onClick={() => setIsAddCustomLineItemOpen(true)}
                    >
                      <Plus className="size-3.5" />
                      Add custom line item
                    </Button>
                  </div>
                }
              />
              ) : null}
            </ResultsPanelHeader>
          }
        >
              <Table
                variant="panel"
                className="table-fixed min-w-full"
                style={{
                  minWidth: `${
                    FIELD_PROGRESS_TABLE_BASE_WIDTH_REM +
                    (showScheduleColumns ? FIELD_PROGRESS_TABLE_SCHEDULE_WIDTH_REM : 0)
                  }rem`,
                }}
              >
                <colgroup>
                  <col
                    style={{ width: `${FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.actions}rem` }}
                  />
                  <col
                    style={{ width: `${FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.task}rem` }}
                  />
                  <col
                    style={{ width: `${FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.total}rem` }}
                  />
                  {showScheduleColumns ? (
                    <>
                      {Array.from({ length: 5 }, (_, index) => (
                        <col
                          key={`schedule-column-${index}`}
                          style={{
                            width: `${FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.schedule}rem`,
                          }}
                        />
                      ))}
                      <col
                        style={{
                          width: `${FIELD_PROGRESS_TABLE_COLUMN_WIDTHS_REM.scheduleProgress}rem`,
                        }}
                      />
                    </>
                  ) : null}
                  <col />
                </colgroup>
                <TableHeader>
                  {/* Band row: names the attached schedule columns so they read as one group
                      rather than six more columns bolted onto the task columns. */}
                  {showScheduleColumns ? (
                    <TableRow className="hover:bg-transparent">
                      {/* The task columns are the table's subject and need no band label. */}
                      <TableHead colSpan={3} className="h-7 py-1" />
                      <TableHead
                        colSpan={6}
                        className={cn(FIELD_PILOT_COLUMN_BAND, "h-7 py-1 text-muted-foreground")}
                      >
                        Schedule
                      </TableHead>
                      <TableHead
                        className={cn(FIELD_PILOT_COLUMN_BAND, "h-7 py-1 text-muted-foreground")}
                      >
                        Status
                      </TableHead>
                    </TableRow>
                  ) : null}
                  <TableRow>
                    <TableHead className="h-8 w-8 px-1.5 py-1">
                      <span className="sr-only">Actions</span>
                    </TableHead>
                    <TableHead>Task</TableHead>
                    <TableHead>Total</TableHead>
                    {showScheduleColumns ? (
                      <>
                        <TableHead className={FIELD_PILOT_COLUMN_BAND}>Start</TableHead>
                        <TableHead>Finish</TableHead>
                        <TableHead>Duration</TableHead>
                        <TableHead title="Expected progress toward final completion as of today, following start and milestone dates">
                          Expected %
                        </TableHead>
                        <TableHead title="Actual progress toward final completion from item statuses and dated milestone stages">
                          Actual %
                        </TableHead>
                        <TableHead className="min-w-[140px]">Progress</TableHead>
                      </>
                    ) : null}
                    <TableHead className={showScheduleColumns ? FIELD_PILOT_COLUMN_BAND : undefined}>
                      Status Breakdown
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {visibleProgressRows.map((row, rowIndex) => {
                    const scheduleRow = scheduleByTagId.get(String(row.tagId));
                    const behind = showScheduleColumns && (scheduleRow?.isBehind ?? false);
                    const subTasks = scheduleRow?.subTasks ?? [];
                    const {
                      items: soloSubTasks,
                      groups: groupSubTasks,
                      hasChildRows,
                    } = splitTaskChildRows(subTasks, scheduleRow?.ungrouped);
                    const isExpanded = expandedTagIds.has(String(row.tagId));
                    const locationSummary = summarizeLocationsForTag(
                      tagLocations,
                      row.tagId,
                      {
                        isTruncated: tagLocationsTruncated,
                        totalCount: tagLocationsTotalCount,
                        byTag: tagLocationsByTag,
                      }
                    );
                    const convertAction = formatConvertActionLabel(locationSummary);
                    const canNavigateInstances =
                      layerVisibility.tags &&
                      navigableTagIds.includes(String(row.tagId));
                    const measurementType =
                      row.measurementType ?? measurementTypeByTagId.get(String(row.tagId)) ?? null;
                    return (
                    <Fragment key={row.tagId}>
                    <TableRow
                      ref={(node) => {
                        if (node) {
                          progressRowRefs.current.set(String(row.tagId), node);
                        } else {
                          progressRowRefs.current.delete(String(row.tagId));
                        }
                      }}
                      className={fieldPilotRowClasses({
                        kind: "task",
                        isBehind: behind,
                        // Every task after the first opens its group with a heavy rule, so a task
                        // and the sub-rows above it can never be mistaken for one another.
                        startsGroup: rowIndex > 0,
                      })}
                    >
                      <TableCell className="w-8 px-1.5 py-0 align-middle">
                        <ProgressRowActionsMenu
                          tagLabel={row.rawValue}
                          convertAction={convertAction}
                          progressMode={row.progressMode}
                          onEditSchedule={() => setScheduleEditTagId(row.tagId)}
                          onConvert={() => setConvertDialogTagId(row.tagId)}
                          onProgressModeChange={(progressMode) => {
                            void setProgressMode({
                              tagId: row.tagId,
                              progressMode,
                              measurementType: measurementType ?? undefined,
                            }).catch((error) => {
                              notifyError(error,
                                getUserFacingErrorMessage(error, "Failed to update progress mode")
                              );
                            });
                          }}
                        />
                      </TableCell>
                      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "align-middle")}>
                        <FieldPilotTreeCellContent
                          kind="task"
                          isExpanded={isExpanded}
                          onToggle={
                            hasChildRows ? () => toggleExpandTag(String(row.tagId)) : undefined
                          }
                          toggleLabel={
                            isExpanded
                              ? "Hide items and sub-tasks"
                              : "Show items and sub-tasks"
                          }
                          trailing={
                            <div className="flex shrink-0 items-center gap-px">
                              <TagInstanceNavigationButton
                                direction="previous"
                                tagLabel={row.rawValue}
                                disabled={!canNavigateInstances}
                                onClick={() => focusPreviousInstanceForTag(row.tagId)}
                              />
                              <TagInstanceNavigationButton
                                direction="next"
                                tagLabel={row.rawValue}
                                disabled={!canNavigateInstances}
                                onClick={() => focusNextInstanceForTag(row.tagId)}
                              />
                            </div>
                          }
                        >
                          <button
                            type="button"
                            className="block max-w-full truncate text-left text-sm font-semibold hover:underline"
                            onClick={() => highlightAndFocusTag(row.tagId)}
                          >
                            {scheduleRow?.displayName ?? row.rawValue}
                          </button>
                          {scheduleRow && scheduleRow.displayName !== row.rawValue ? (
                            <span className="block truncate text-[10px] font-normal text-muted-foreground">
                              {row.rawValue}
                            </span>
                          ) : null}
                        </FieldPilotTreeCellContent>
                      </TableCell>
                      <TableCell className={cn(FIELD_PILOT_ROW_CELL, "tabular-nums")}>
                        {formatProgressValue(row.total, {
                          progressMode: row.progressMode,
                          measurementType,
                        })}
                      </TableCell>
                      {showScheduleColumns ? (
                        <ScheduleColumns scheduleRow={scheduleRow} />
                      ) : null}
                      <TableCell
                        className={cn(
                          FIELD_PILOT_ROW_CELL,
                          showScheduleColumns && FIELD_PILOT_COLUMN_BAND,
                          "min-w-[200px] align-middle"
                        )}
                      >
                        <StatusBreakdownBar breakdown={row.statusBreakdown} />
                      </TableCell>
                    </TableRow>
                    {/* Item schedules first, then grouped sub-tasks — both revealed together by
                        the task's toggle. */}
                    {isExpanded && soloSubTasks.length > 0 ? (
                      <SubTaskRows
                        subTasks={soloSubTasks}
                        layout={{ variant: "progress", showScheduleColumns }}
                        onEditSubTask={openEditSubTaskDialog}
                        onDeleteSubTask={handleDeleteEmptySubTask}
                      />
                    ) : null}
                    {isExpanded && groupSubTasks.length > 0 ? (
                      <SubTaskRows
                        subTasks={groupSubTasks}
                        layout={{ variant: "progress", showScheduleColumns }}
                        onEditSubTask={openEditSubTaskDialog}
                        onDeleteSubTask={handleDeleteEmptySubTask}
                      />
                    ) : null}
                    {/* The ungrouped remainder closes the group, so the children above it plus
                        this row account for the whole task. */}
                    {isExpanded && scheduleRow?.ungrouped ? (
                      <UngroupedRow
                        ungrouped={scheduleRow.ungrouped}
                        layout={{ variant: "progress", showScheduleColumns }}
                        onGroup={() =>
                          openGroupUngroupedDialog(
                            row.tagId,
                            scheduleRow?.displayName ?? row.rawValue,
                            scheduleRow?.ungrouped?.itemCount ?? 0
                          )
                        }
                      />
                    ) : null}
                    </Fragment>
                    );
                  })}
                </TableBody>
              </Table>
        </ResizableBottomPanel>
        ) : null}

      <FloatingPanelToggle
        isOpen={isPanelOpen}
        onClick={() => setIsPanelOpen(true)}
        tagCount={activeTags.length}
        label="Status Progress"
      />

      <FieldPilotDailyLogsDialog
        jobId={routeJobId}
        jobName={details.job.name}
        open={isDailyLogsDialogOpen}
        onOpenChange={setIsDailyLogsDialogOpen}
      />

      <FieldPilotAddCustomLineItemDialog
        open={isAddCustomLineItemOpen}
        onOpenChange={setIsAddCustomLineItemOpen}
        onCreate={handleCreateCustomLineItem}
      />

      <SearchDocumentTagsDialog
        open={isSearchTagsOpen}
        onOpenChange={setIsSearchTagsOpen}
        onSearchDocumentTags={handleSearchDocumentTags}
        onCreateCustomLineItemsFromDocumentTags={handleCreateFromDocumentTags}
        emptyStateMessages={FIELD_PILOT_TAG_SEARCH_EMPTY_MESSAGES}
        description="Search exact normalized tags in this FieldPilot plan set and add them as Measurement count tasks."
      />

      {convertDialogTag && convertDialogSummary ? (
        <ConvertTagPointsDialog
          open={convertDialogTagId !== null}
          onOpenChange={(open) => {
            if (!open) setConvertDialogTagId(null);
          }}
          tagLabel={convertDialogTag.rawValue}
          defaultStatusName={defaultStatusName}
          unconvertedLocations={convertDialogSummary.unconverted}
          documentCount={convertDialogSummary.unconvertedDocumentCount}
          incompleteDocumentCount={incompletePlanDocumentCount}
          isTruncated={convertDialogSummary.isTruncated}
          resultTotalCount={convertDialogSummary.resultTotalCount}
          onConvertChunk={async (detectionIds) => {
            if (!routeJobId || !convertDialogTagId) {
              return {
                createdItemIds: [],
                skippedExistingItemIds: [],
                skippedInactiveDetectionCount: 0,
              };
            }
            return await convertTagDetectionsToPoints({
              jobId: routeJobId,
              tagId: convertDialogTagId,
              detectionIds,
            });
          }}
          onChunkCreated={(createdItemIds) => {
            recordCreatedPointsHistory(createdItemIds);
          }}
          onComplete={({ createdItemIds, skippedExistingCount }) => {
            enableMeasurements();
            enableTags();
            const createdLabel =
              createdItemIds.length === 1
                ? "Created 1 point"
                : `Created ${createdItemIds.length} points`;
            if (skippedExistingCount > 0) {
              toast.success(
                `${createdLabel}; ${skippedExistingCount} already existed`
              );
            } else {
              toast.success(createdLabel);
            }
            setConvertDialogTagId(null);
          }}
        />
      ) : null}

      <FieldPilotScheduleDialog
        jobId={routeJobId ?? null}
        jobName={details.job.name}
        open={isScheduleDialogOpen}
        onOpenChange={setIsScheduleDialogOpen}
      />

      {scheduleEditTag ? (
        <FieldPilotTagScheduleDialog
          open
          onOpenChange={(open) => {
            if (!open) setScheduleEditTagId(null);
          }}
          onOpenFullSchedule={() => {
            setScheduleEditTagId(null);
            setIsScheduleDialogOpen(true);
          }}
          tagId={scheduleEditTag._id}
          rawValue={scheduleEditTag.rawValue}
          displayName={
            scheduleEditRow?.displayName ??
            scheduleEditTag.displayName ??
            scheduleEditTag.rawValue
          }
          startDateKey={
            scheduleEditRow?.startDateKey ?? scheduleEditTag.startDateKey ?? null
          }
          finishDateKey={
            scheduleEditRow?.finishDateKey ?? scheduleEditTag.finishDateKey ?? null
          }
        />
      ) : null}

      {subTaskDialogTarget ? (
        <FieldPilotSubTaskDialog
          open={isSubTaskDialogOpen}
          onOpenChange={setIsSubTaskDialogOpen}
          jobId={routeJobId}
          statuses={details.statuses}
          target={subTaskDialogTarget}
          onEditParentSchedule={() => setIsScheduleDialogOpen(true)}
        />
      ) : null}

      <ExportDialog
        open={isExportDialogOpen}
        onOpenChange={handleExportDialogOpenChange}
        description={details?.job?.name}
        extension="pdf"
        defaultFileName={fieldReportName}
        optionsSummary={saveAsDailyLog ? "Daily Log Entry" : undefined}
        options={
          <>
            <ExportOptionGroup
              title="Report Content"
              hint={
                !includeSchedule && !includeProgressBars && !includeGanttChart && !includeMeasurementBreakdown
                  ? "No report sections selected — the export will include the marked-up drawing pages only."
                  : undefined
              }
            >
              <ExportOptionCheckbox checked={includeSchedule} onCheckedChange={setIncludeSchedule}>
                Schedule table
              </ExportOptionCheckbox>
              <ExportOptionCheckbox checked={includeProgressBars} onCheckedChange={setIncludeProgressBars}>
                Progress bars
              </ExportOptionCheckbox>
              <ExportOptionCheckbox checked={includeGanttChart} onCheckedChange={setIncludeGanttChart}>
                Gantt chart page
              </ExportOptionCheckbox>
              <ExportOptionCheckbox checked={includeMeasurementBreakdown} onCheckedChange={setIncludeMeasurementBreakdown}>
                Measurement breakdown table
              </ExportOptionCheckbox>
            </ExportOptionGroup>

            <ExportOptionGroup
              title="Plan Markup"
              hint={
                exportLabelAnnotation === "next"
                  ? "Each label adds the next status and its due date, or the final status and date when the item is complete."
                  : exportLabelAnnotation === "all"
                    ? "Each label lists every milestone and due date, one per line under the item name."
                    : exportLabelAnnotation === "current"
                      ? "Each label shows its current status under the item name."
                      : "Labels show the item name only."
              }
            >
              <ExportOptionRow label="Color by">
                <SegmentedControl
                  aria-label="Color measurements by"
                  size="compact"
                  value={exportColorizeBy}
                  onValueChange={setExportColorizeBy}
                  options={COLORIZE_BY_OPTIONS}
                />
              </ExportOptionRow>
              <ExportOptionRow label="Status label">
                <Select
                  value={exportLabelAnnotation}
                  onValueChange={(value) => setExportLabelAnnotation(value as FieldPilotAnnotationMode)}
                >
                  <SelectTrigger className="h-8 w-28" aria-label="Status label">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="current">Current</SelectItem>
                    <SelectItem value="off">Off</SelectItem>
                    <SelectItem value="next">Next</SelectItem>
                    <SelectItem value="all">All</SelectItem>
                  </SelectContent>
                </Select>
              </ExportOptionRow>
              <ExportOptionCheckbox checked={exportShowMeasurementValues} onCheckedChange={setExportShowMeasurementValues}>
                Label measurement values
              </ExportOptionCheckbox>
            </ExportOptionGroup>

            <ExportOptionGroup
              title="Status Filter"
              hint="Limit the report and plan markup to a single status, or keep all statuses."
            >
              <Select value={exportStatusFilterId} onValueChange={setExportStatusFilterId}>
                <SelectTrigger aria-label="Status filter">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All statuses</SelectItem>
                  {details.statuses.map((status) => (
                    <SelectItem key={status._id} value={status._id}>
                      <StatusOptionLabel colorHex={status.colorHex} name={status.name} />
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </ExportOptionGroup>

            <ExportOptionGroup title="Output">
              <ExportOptionCheckbox checked={saveAsDailyLog} onCheckedChange={setSaveAsDailyLog}>
                Add as today's daily log entry
              </ExportOptionCheckbox>
            </ExportOptionGroup>
          </>
        }
        lastFile={lastFileFromArtifact({
          artifact: pdfExport,
          canDownload: Boolean(pdfExportDownloadUrl),
          download: handleDownloadPdf,
          saveName: (fileName) =>
            pdfExport ? exportFilenames.saveForArtifact(pdfExport.artifactId, fileName) : Promise.resolve(),
        })}
        notice={canEdit ? null : "Reviewers can download the last PDF but can't generate a new one."}
        onExport={handleExportPdf}
        isExporting={isExporting}
        exportDisabled={!canEdit || !routeJobId || isPdfExportInProgress}
      />

      <Dialog open={isManageStatusesOpen} onOpenChange={setIsManageStatusesOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Manage statuses</DialogTitle>
            <DialogDescription>
              Reorder the status pipeline. Items move up the pipeline in this order — the first
              status is the starting stage.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2">
            {(details.statuses ?? []).length === 0 ? (
              <p className="text-sm text-muted-foreground">No statuses yet.</p>
            ) : (
              (details.statuses ?? []).map((status, index) => (
                <FieldPilotStatusEditorRow
                  key={status._id}
                  status={status}
                  index={index}
                  total={(details.statuses ?? []).length}
                  onUpdateStatus={handleUpdateStatusDefinition}
                  onReorderStatus={handleReorderStatus}
                  onDeleteStatus={handleDeleteStatusDefinition}
                />
              ))
            )}
          </div>
          <div className="space-y-2 border-t pt-3">
            <Label className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
              Add status
            </Label>
            <p className="text-xs text-muted-foreground">
              New statuses join the end of the pipeline. The color drives the drawing overlay.
            </p>
            <div className="flex items-center gap-2">
              <Input
                value={newStatusName}
                onChange={(event) => setNewStatusName(event.target.value)}
                placeholder="e.g. Punch List"
                className="h-9 flex-1"
                aria-label="New status name"
                onKeyDown={(event) => {
                  if (event.key === "Enter") {
                    event.preventDefault();
                    void handleCreateStatus();
                  }
                }}
              />
              <ColorPicker
                value={newStatusColor}
                onChange={setNewStatusColor}
                ariaLabel="New status color"
              />
              <Button
                onClick={() => void handleCreateStatus()}
                disabled={isCreatingStatus || !newStatusName.trim()}
              >
                <Plus className="size-4" />
                {isCreatingStatus ? "Adding..." : "Add"}
              </Button>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsManageStatusesOpen(false)}>
              Done
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
